import {
  ASCENSION_CRYSTAL_COST,
  CRYSTAL_WAGE_PER_MINUTE,
  DIFFICULTIES,
  MODULES,
  SYNERGIES,
  TEAM_TECH,
  describeRequirement,
  evaluateSynergies,
  formatAttackIntervalSeconds,
  getHiddenEffect,
  getHiddenSynergy,
  getModule,
  REFORGE_CRYSTAL_COSTS,
  UPGRADE_GOLD_COSTS,
  getUndeadUnit,
  type DamageSourceKind,
  type ModuleDefinition,
  type ModuleSlotRuntimeState,
  type DifficultyId,
  type LaneId,
  type PlaytestClientMetrics,
  type PlaytestInputMethod,
  type PlaytestSubmission,
  type PlaytestSurvey,
  type PlayerPublicState,
  type PublicRoomState,
  type ServerMessage,
  type UpgradeOffer,
} from "../../../../packages/game-core/src";
import {
  GameNetworkClient,
  type ConnectionStatus,
} from "../network/GameNetworkClient";
import { createOfferSynergyRecipes } from "./offerPresentation";
import { renderTieredModuleText } from "./tierPresentation";

interface ClientMetricSample {
  fpsP5: number;
  tier: "low" | "balanced" | "high";
  renderedEnemies: number;
  inputMethod: PlaytestInputMethod;
}

interface MetaProgress {
  runs: number;
  wins: number;
  embers: number;
  unlocks: string[];
  settledRooms: string[];
}

export class AppController {
  private state?: PublicRoomState;
  private offer?: UpgradeOffer;
  private lastLobbySignature = "";
  private lastBuildSignature = "";
  private lastDamageSignature = "";
  private lastRenderedPhase?: PublicRoomState["phase"];
  private buildCollapsed = false;
  private activeBuildPage =
    "skills" as
      | "skills"
      | "attributes"
      | "synergies"
      | "construction"
      | "damage";
  private tacticsCollapsed = false;
  private offerCollapsed = false;
  private autoReady = false;
  private clientMetrics?: PlaytestClientMetrics;
  private readonly fpsSamples: number[] = [];
  private readonly testerId = getOrCreateTesterId();
  private settledRoomCode?: string;
  private metaProgress = readMetaProgress();
  private readonly discoveredModules = readDiscoverySet(
    "bastion.phase3.discoveredModules",
  );
  private readonly discoveredSynergies = readDiscoverySet(
    "bastion.phase3.discoveredSynergies",
  );

  private readonly connectPanel = element<HTMLElement>("connect-panel");
  private readonly connectError = element<HTMLElement>("connect-error");
  private readonly playerName = element<HTMLInputElement>("player-name");
  private readonly roomCodeInput = element<HTMLInputElement>("room-code-input");
  private readonly runSeedInput = element<HTMLInputElement>("run-seed-input");
  private readonly resumeButton = element<HTMLButtonElement>("resume-session");
  private readonly lobbyPanel = element<HTMLElement>("lobby-panel");
  private readonly battleHud = element<HTMLElement>("battle-hud");
  private readonly playerVitals = element<HTMLElement>("player-vitals");
  private readonly buildPanel = element<HTMLElement>("build-panel");
  private readonly openBuildButton = element<HTMLButtonElement>("open-build");
  private readonly offerPanel = element<HTMLElement>("offer-panel");
  private readonly openOfferButton =
    element<HTMLButtonElement>("open-offer");
  private readonly lanePanel = element<HTMLElement>("lane-panel");
  private readonly openTacticsButton =
    element<HTMLButtonElement>("open-tactics");
  private readonly guidePanel = element<HTMLElement>("guide-panel");
  private readonly resultPanel = element<HTMLElement>("result-panel");
  private readonly codexPanel = element<HTMLElement>("codex-panel");

  constructor(private readonly network: GameNetworkClient) {
    this.bindActions();
    this.renderMetaSummary();
    this.network.onMessage((message) => this.handleMessage(message));
    this.network.onStatus((status) => this.renderConnection(status));
    window.addEventListener("bastion-client-metrics", (event) => {
      const sample = (event as CustomEvent<ClientMetricSample>).detail;
      this.fpsSamples.push(sample.fpsP5);
      this.clientMetrics = {
        tier: sample.tier,
        fpsP5Latest: sample.fpsP5,
        fpsP5Minimum: Math.min(...this.fpsSamples),
        fpsP5Average: Math.round(
          this.fpsSamples.reduce((sum, value) => sum + value, 0) /
            this.fpsSamples.length,
        ),
        sampleCount: this.fpsSamples.length,
        maxRenderedEnemies: Math.max(
          sample.renderedEnemies,
          this.clientMetrics?.maxRenderedEnemies ?? 0,
        ),
        inputMethod: sample.inputMethod,
        viewport: `${window.innerWidth}x${window.innerHeight}`,
        userAgent: navigator.userAgent,
        touchPoints: navigator.maxTouchPoints,
      };
      this.renderHud();
    });
    const query = new URLSearchParams(window.location.search);
    if (query.get("guide") === "0") {
      localStorage.setItem("bastion.phase3.guideSeen", "1");
    }
    if (query.get("codex") === "1") {
      this.renderCodex();
      this.codexPanel.classList.remove("hidden");
    }
    this.runSeedInput.value = query.get("seed")?.slice(0, 96) ?? "";
    element<HTMLSelectElement>("survey-device").value = /Android/i.test(
      navigator.userAgent,
    )
      ? "mid_android"
      : navigator.maxTouchPoints > 0
        ? "other_mobile"
        : "desktop";

    const previousName = localStorage.getItem("bastion.phase1.playerName");
    if (previousName && !this.playerName.readOnly) {
      this.playerName.value = previousName;
    }
    const storedSession = this.network.getStoredSession();
    this.resumeButton.classList.toggle("hidden", !storedSession);
    if (storedSession) {
      window.setTimeout(() => this.network.resumeSession(), 150);
    } else {
      const roomCode = query
        .get("room")
        ?.toUpperCase()
        .replace(/[^A-Z0-9]/g, "")
        .slice(0, 5);
      if (roomCode?.length === 5) {
        const invitedName = query.get("name")?.slice(0, 16) || this.readName();
        this.playerName.value = invitedName;
        this.autoReady = query.get("ready") === "1";
        window.setTimeout(
          () => this.network.joinRoom(roomCode, invitedName),
          150,
        );
      } else if (query.get("autocreate") === "1" && this.runSeedInput.value) {
        window.setTimeout(
          () =>
            this.network.createRoom(
              this.readName(),
              this.runSeedInput.value,
            ),
          150,
        );
      }
    }
  }

  private bindActions(): void {
    for (const button of document.querySelectorAll<HTMLButtonElement>(
      "[data-open-codex]",
    )) {
      button.addEventListener("click", () => {
        this.renderCodex();
        this.codexPanel.classList.remove("hidden");
        element<HTMLButtonElement>("close-codex").focus();
      });
    }
    element<HTMLButtonElement>("close-codex").addEventListener("click", () => {
      this.codexPanel.classList.add("hidden");
    });
    window.addEventListener("keydown", (event) => {
      if (event.key === "Escape") {
        this.codexPanel.classList.add("hidden");
      }
    });

    element<HTMLButtonElement>("create-room").addEventListener("click", () => {
      this.clearError();
      const name = this.readName();
      this.network.createRoom(name, this.runSeedInput.value);
    });

    element<HTMLButtonElement>("join-room").addEventListener("click", () => {
      this.clearError();
      const code = this.roomCodeInput.value
        .toUpperCase()
        .replace(/[^A-Z0-9]/g, "")
        .slice(0, 5);
      this.roomCodeInput.value = code;
      if (code.length !== 5) {
        this.setError("请输入五位房间码。");
        return;
      }
      this.network.joinRoom(code, this.readName());
    });

    this.resumeButton.addEventListener("click", () => {
      this.clearError();
      if (!this.network.resumeSession()) {
        this.setError("没有可恢复的本地会话。");
      }
    });

    this.roomCodeInput.addEventListener("input", () => {
      this.roomCodeInput.value = this.roomCodeInput.value
        .toUpperCase()
        .replace(/[^A-Z0-9]/g, "")
        .slice(0, 5);
    });

    element<HTMLButtonElement>("copy-room-code").addEventListener("click", async () => {
      if (!this.state) {
        return;
      }
      const copied = await copyText(this.state.roomCode);
      this.notice(
        copied
          ? `房间码 ${this.state.roomCode} 已复制`
          : `房间码 ${this.state.roomCode}（当前浏览器禁止自动复制）`,
      );
    });

    element<HTMLButtonElement>("ready-button").addEventListener("click", () => {
      const local = this.getLocalPlayer();
      if (local) {
        this.network.send({ type: "ready", ready: !local.ready });
      }
    });

    element<HTMLButtonElement>("start-button").addEventListener("click", () => {
      const difficulty = element<HTMLSelectElement>("difficulty")
        .value as DifficultyId;
      this.network.send({ type: "start", difficulty });
    });

    element<HTMLButtonElement>("toggle-build").addEventListener("click", () => {
      this.buildCollapsed = true;
      this.syncBattlePanelVisibility();
    });

    this.openBuildButton.addEventListener("click", () => {
      this.buildCollapsed = false;
      this.syncBattlePanelVisibility();
    });

    element<HTMLButtonElement>("open-damage").addEventListener("click", () => {
      this.activeBuildPage = "damage";
      this.buildCollapsed = false;
      this.syncBattlePanelVisibility();
    });

    for (const button of document.querySelectorAll<HTMLButtonElement>(
      "[data-build-tab]",
    )) {
      button.addEventListener("click", () => {
        this.activeBuildPage = button.dataset
          .buildTab as typeof this.activeBuildPage;
        this.syncBuildPage();
      });
    }

    element<HTMLButtonElement>("close-tactics").addEventListener("click", () => {
      this.tacticsCollapsed = true;
      this.syncBattlePanelVisibility();
    });

    this.openTacticsButton.addEventListener("click", () => {
      this.tacticsCollapsed = false;
      this.syncBattlePanelVisibility();
    });

    element<HTMLButtonElement>("close-offer").addEventListener("click", () => {
      this.offerCollapsed = true;
      this.offerPanel.classList.add("hidden");
      this.openOfferButton.classList.toggle("hidden", !this.offer);
    });

    this.openOfferButton.addEventListener("click", () => {
      this.offerCollapsed = false;
      this.renderOffer();
    });

    element<HTMLButtonElement>("reroll-offer").addEventListener("click", () => {
      if (this.offer) {
        this.network.send({ type: "rerollOffer", offerId: this.offer.id });
      }
    });

    element<HTMLButtonElement>("abandon-offer").addEventListener("click", () => {
      if (
        this.offer?.canAbandon &&
        window.confirm(
          `放弃后已支付的 ${this.offer.scrapCost} 金币不会返还。\n\n` +
            "下一次再次升阶时会立即重新随机一组三选一候选。",
        )
      ) {
        this.network.send({
          type: "cancelOffer",
          offerId: this.offer.id,
        });
      }
    });

    element<HTMLButtonElement>("assist-signal").addEventListener("click", () => {
      const laneId = this.getLocalLaneId();
      if (laneId) {
        this.network.send({ type: "teamSignal", kind: "assist", laneId });
      }
    });

    element<HTMLButtonElement>("objective-signal").addEventListener("click", () => {
      const objective = this.state?.objectives.find(
        (candidate) => candidate.status === "active",
      );
      const laneId = objective
        ? this.getClosestLaneId(objective.x, objective.y)
        : this.getLocalLaneId();
      if (laneId) {
        this.network.send({ type: "teamSignal", kind: "objective", laneId });
      }
    });

    element<HTMLButtonElement>("close-guide").addEventListener("click", () => {
      localStorage.setItem("bastion.phase3.guideSeen", "1");
      this.guidePanel.classList.add("hidden");
    });

    element<HTMLButtonElement>("new-room-button").addEventListener("click", () => {
      this.network.close();
      this.network.clearSession();
      window.location.reload();
    });

    element<HTMLButtonElement>("replay-seed-button").addEventListener(
      "click",
      () => {
        if (!this.state) {
          return;
        }
        this.network.close();
        this.network.clearSession();
        const nextUrl = new URL(window.location.href);
        const serverOverride = nextUrl.searchParams.get("server");
        nextUrl.search = "";
        if (serverOverride) {
          nextUrl.searchParams.set("server", serverOverride);
        }
        nextUrl.searchParams.set("seed", this.state.runSeed);
        nextUrl.searchParams.set("autocreate", "1");
        window.location.assign(nextUrl);
      },
    );

    element<HTMLButtonElement>("copy-playtest-report").addEventListener(
      "click",
      async () => {
        if (!this.state) {
          return;
        }
        const copied = await copyText(
          JSON.stringify(
            createTechnicalReport(this.state, this.clientMetrics),
            null,
            2,
          ),
        );
        this.notice(
          copied ? "本局测试报告已复制" : "当前浏览器禁止自动复制测试报告",
          !copied,
        );
      },
    );

    element<HTMLButtonElement>("submit-playtest-report").addEventListener(
      "click",
      () => void this.submitPlaytestReport(),
    );
  }

  private handleMessage(message: ServerMessage): void {
    switch (message.type) {
      case "welcome":
        this.state = mergeState(this.state, message.state);
        this.offer = message.offer;
        this.offerCollapsed = false;
        this.connectPanel.classList.add("hidden");
        this.render();
        if (this.autoReady && message.state.phase === "lobby") {
          this.autoReady = false;
          this.network.send({ type: "ready", ready: true });
        }
        if (message.resumed) {
          this.notice("已恢复原房间与完整构筑状态");
        }
        break;
      case "snapshot":
        this.state = mergeState(this.state, message.state);
        this.render();
        break;
      case "offer":
        this.offer = message.offer;
        this.offerCollapsed = false;
        this.openOfferButton.classList.add("hidden");
        this.renderOffer();
        break;
      case "offerCleared":
        this.offer = undefined;
        this.offerCollapsed = false;
        this.offerPanel.classList.add("hidden");
        this.openOfferButton.classList.add("hidden");
        break;
      case "notice":
        this.notice(message.message);
        break;
      case "teamSignal":
        this.notice(
          `${message.signal.playerName}：${signalLabel(message.signal.kind)} · ${laneName(message.signal.laneId)}`,
          message.signal.kind === "danger",
        );
        break;
      case "result":
        this.notice(message.reason);
        break;
      case "error":
        this.setError(message.message);
        this.notice(message.message, true);
        if (message.code === "HANDSHAKE_REJECTED") {
          this.network.clearSession();
          this.connectPanel.classList.remove("hidden");
        }
        break;
      case "enemySpawn":
      case "enemyDeath":
      case "shot":
      case "abilityEntities":
      case "surfaceMining":
      case "surfaceMiningDevour":
      case "archaeology":
      case "pong":
        break;
    }
  }

  private render(): void {
    if (!this.state) {
      return;
    }
    const inLobby = this.state.phase === "lobby";
    const inBattle = this.state.phase === "playing";
    const ended =
      this.state.phase === "victory" ||
      this.state.phase === "draw" ||
      this.state.phase === "defeat";
    const enteringBattle =
      inBattle && this.lastRenderedPhase !== this.state.phase;
    if (enteringBattle) {
      const compactBattle = window.matchMedia(
        "(max-width: 760px), (orientation: landscape) and (max-height: 560px)",
      ).matches;
      const requestedPanel = new URLSearchParams(window.location.search).get(
        "panel",
      );
      this.buildCollapsed = compactBattle && requestedPanel !== "build";
      this.tacticsCollapsed = compactBattle;
      this.codexPanel.classList.add("hidden");
    }
    this.lastRenderedPhase = this.state.phase;
    document.body.dataset.phase = this.state.phase;

    this.lobbyPanel.classList.toggle("hidden", !inLobby);
    this.battleHud.classList.toggle("hidden", !inBattle && !ended);
    this.playerVitals.classList.toggle("hidden", !inBattle);
    const showGuide =
      inLobby && localStorage.getItem("bastion.phase3.guideSeen") !== "1";
    this.guidePanel.classList.toggle("hidden", !showGuide);
    this.syncBattlePanelVisibility();
    this.resultPanel.classList.toggle("hidden", !ended);

    this.updateDiscoveries();
    this.renderLobby();
    this.renderHud();
    this.renderLanes();
    this.renderBuild();
    this.renderObjective();
    if (this.offer && inBattle && !this.offerCollapsed) {
      this.renderOffer();
    } else {
      this.offerPanel.classList.add("hidden");
      this.openOfferButton.classList.toggle(
        "hidden",
        !this.offer || !inBattle || !this.offerCollapsed,
      );
    }
    if (ended) {
      this.renderResult();
    }
  }

  private renderLobby(): void {
    if (!this.state || this.state.phase !== "lobby") {
      return;
    }
    const signature = JSON.stringify([
      this.network.playerId,
      this.state.hostId,
      ...this.state.players.map((player) => [
        player.id,
        player.name,
        player.ready,
        player.connected,
      ]),
    ]);
    if (signature === this.lastLobbySignature) {
      return;
    }
    this.lastLobbySignature = signature;

    element<HTMLButtonElement>("copy-room-code").textContent = this.state.roomCode;
    element<HTMLElement>("lobby-capacity").textContent =
      `${this.state.players.length} / 4`;

    const list = element<HTMLElement>("lobby-players");
    list.replaceChildren(
      ...this.state.players.map((player) => {
        const row = document.createElement("div");
        row.className = "player-row";
        const name = document.createElement("strong");
        name.textContent =
          `${player.name}${player.id === this.state?.hostId ? " · 房主" : ""}`;
        const meta = document.createElement("span");
        meta.className = "player-meta";
        meta.textContent = player.connected
          ? player.ready
            ? "已准备"
            : "未准备"
          : "AI 接管";
        row.append(name, meta);
        return row;
      }),
    );

    const local = this.getLocalPlayer();
    const readyButton = element<HTMLButtonElement>("ready-button");
    readyButton.textContent = local?.ready ? "取消准备" : "准备";
    const isHost = this.network.playerId === this.state.hostId;
    const startButton = element<HTMLButtonElement>("start-button");
    startButton.classList.toggle("hidden", !isHost);
    startButton.disabled = this.state.players.some((player) => !player.ready);
    const difficulty = element<HTMLSelectElement>("difficulty");
    difficulty.disabled = !isHost;
  }

  private renderHud(): void {
    if (!this.state) {
      return;
    }
    const local = this.getLocalPlayer();
    const remaining = Math.max(0, this.state.durationMs - this.state.elapsedMs);
    element<HTMLElement>("battle-timer").textContent = formatTime(remaining);
    const coreRatio = Math.max(
      0,
      this.state.core.health / this.state.core.maxHealth,
    );
    element<HTMLElement>("core-health").textContent =
      `${Math.ceil(coreRatio * 100)}% · 护甲 ${this.state.core.armor}`;
    element<HTMLElement>("core-health-fill").style.width =
      `${coreRatio * 100}%`;
    element<HTMLElement>("scrap-value").textContent = String(local?.scrap ?? 0);
    element<HTMLElement>("core-value").textContent = String(local?.core ?? 0);
    element<HTMLElement>("level-value").textContent = local
      ? String(local.level)
      : "1";
    element<HTMLElement>("experience-value").textContent = local
      ? local.level >= local.levelCap
        ? "灵息已满"
        : `灵息 ${Math.floor(
            (local.experience / local.nextLevelExperience) * 100,
          )}%`
      : "灵息 0%";
    element<HTMLElement>("mana-value").textContent = local
      ? `${Math.floor(local.mana)} · +${local.combatStats.manaRegen.toFixed(2)}/秒`
      : "0";
    element<HTMLElement>("mana-fill").style.width = local
      ? `${Math.max(0, (local.mana / local.maxMana) * 100)}%`
      : "0%";
    element<HTMLElement>("health-value").textContent = local
      ? String(Math.ceil(local.health))
      : "0";
    element<HTMLElement>("health-fill").style.width = local
      ? `${Math.max(0, (local.health / local.maxHealth) * 100)}%`
      : "0%";
    element<HTMLElement>("wage-value").textContent = local
      ? `+${local.wage}金 · +${CRYSTAL_WAGE_PER_MINUTE}晶`
      : "无";
    element<HTMLElement>("wage-value").title =
      `箭塔待分配战利品：${this.state.defenseEconomy.towerGoldPool}金币`;
    element<HTMLElement>("damage-total-value").textContent = compactNumber(
      local?.contribution.damageDealt ?? 0,
    );
    if (local) {
      const damageSignature = JSON.stringify([
        local.id,
        local.modules,
        local.contribution.damageDealt,
        local.contribution.damageBySource,
      ]);
      if (damageSignature !== this.lastDamageSignature) {
        this.lastDamageSignature = damageSignature;
        this.renderDamageBreakdown(
          element<HTMLElement>("damage-breakdown"),
          local,
        );
      }
    }
    const statusText =
      local?.status === "downed"
        ? `倒地 ${Math.ceil(
            Math.max(0, local.respawnAtMs - this.state.elapsedMs) / 1_000,
          )}s`
        : local?.status === "lich"
          ? local.respawnAtMs > 0
            ? `临时巫妖 ${Math.ceil(
                Math.max(
                  0,
                  local.respawnAtMs - this.state.elapsedMs,
                ) / 1_000,
              )}s`
            : "永久巫妖"
        : "正常";
    element<HTMLElement>("player-status").textContent = statusText;
    element<HTMLElement>("player-status").classList.toggle(
      "hidden",
      statusText === "正常",
    );
    element<HTMLElement>("performance-readout").textContent =
      `tick p95 ${this.state.metrics.tickP95Ms.toFixed(2)} ms\n` +
      `下行 ${this.state.metrics.outgoingKbpsPerClient.toFixed(1)} kbps · ` +
      `敌人 ${this.state.metrics.enemyCount} · 伙伴 ${this.state.metrics.abilityEntityCount}\n` +
      (this.clientMetrics
        ? `FPS p5 ${this.clientMetrics.fpsP5Latest}（最低 ${this.clientMetrics.fpsP5Minimum}）· ` +
          `${renderTierLabel(this.clientMetrics.tier)} · 渲染峰值 ${this.clientMetrics.maxRenderedEnemies}`
        : "正在采样客户端性能");
  }

  private renderLanes(): void {
    if (!this.state) {
      return;
    }
    const container = element<HTMLElement>("lane-buttons");
    const heading = document.querySelector<HTMLElement>(
      ".lane-heading .hud-label",
    );
    const actions = document.querySelector<HTMLElement>(".signal-actions");
    const closeButton = document.querySelector<HTMLElement>("#close-tactics");
    if (heading) {
      heading.textContent = "点击道路发送告急标记";
    }
    actions?.classList.remove("hidden");
    closeButton?.classList.remove("hidden");
    const existing = new Map(
      [...container.querySelectorAll<HTMLButtonElement>("[data-lane]")].map(
        (button) => [button.dataset.lane, button],
      ),
    );
    const buttons = this.state.lanes.map((lane) => {
      const button =
        existing.get(lane.id) ?? document.createElement("button");
      button.type = "button";
      button.className = "lane-button";
      button.dataset.lane = lane.id;
      button.dataset.level =
        lane.pressure >= 42 ? "critical" : lane.pressure >= 20 ? "warning" : "safe";
      button.textContent = `${lane.name} ${lane.pressure}`;
      button.title = `${lane.enemyCount} 个敌人 · ${lane.eliteCount} 个精英/Boss`;
      if (!existing.has(lane.id)) {
        button.addEventListener("click", () => {
          this.network.send({
            type: "teamSignal",
            kind: "danger",
            laneId: lane.id,
          });
        });
      }
      return button;
    });
    container.replaceChildren(...buttons);
  }

  private renderBuild(): void {
    const local = this.getLocalPlayer();
    if (!local || !this.state) {
      return;
    }
    const signature = JSON.stringify([
      local.modules,
      local.moduleRuntimeStates,
      local.lockedSlots,
      local.synergies,
      local.hiddenEffects,
      local.hiddenSynergies,
      local.repeatableHiddenEffects,
      local.hiddenSynergyProgress,
      local.luckyStarCount,
      local.scrap,
      local.core,
      local.level,
      local.talentPoints,
      local.talents,
      local.combatStats,
      local.surfaceMiningState,
      local.wage,
      this.state.teamTech,
      this.state.players.map((player) => [
        player.id,
        player.scrap,
        player.core,
        player.connected,
      ]),
    ]);
    if (signature === this.lastBuildSignature) {
      return;
    }
    this.lastBuildSignature = signature;
    const synergyTab = document.querySelector<HTMLButtonElement>(
      '[data-build-tab="synergies"]',
    );
    if (synergyTab) {
      const totalSynergies =
        local.synergies.length + local.hiddenSynergies.length;
      synergyTab.textContent =
        totalSynergies > 0
          ? `羁绊 ${totalSynergies}`
          : "羁绊";
    }

    const slots = element<HTMLElement>("module-slots");
    slots.replaceChildren(
      ...local.modules.map((moduleId, slot) => {
        const card = document.createElement("article");
        card.className = "module-slot";

        const index = document.createElement("span");
        index.className = "slot-index";
        index.textContent = String(slot + 1);

        const name = document.createElement("div");
        name.className = "slot-name";
        const definition = moduleId ? getModule(moduleId) : undefined;
        const moduleRuntime = local.moduleRuntimeStates.find(
          (state) => state.slot === slot,
        );
        const title = document.createElement("strong");
        renderTieredModuleText(title, definition?.name ?? "空秘印槽");
        const details = document.createElement("small");
        details.textContent = definition
          ? `${definition.tier}阶 · ${abilityCategoryLabel(definition.category)} · ` +
            formatAbilityDetails(
              definition,
              local.synergies.includes("miners_set"),
            ).slice(0, 2).join(" · ")
          : "花费金币进行一阶3选1";
        name.append(title, details);
        if (
          moduleRuntime?.experienceStored !== undefined &&
          moduleRuntime.experienceCapacity !== undefined
        ) {
          const runtime = document.createElement("small");
          runtime.className = "slot-runtime";
          runtime.textContent =
            `已吸收 ${formatRuntimeNumber(moduleRuntime.experienceStored)}/` +
            `${formatRuntimeNumber(moduleRuntime.experienceCapacity)} 经验 · ` +
            `主动替换预计返还 ${formatRuntimeNumber(moduleRuntime.replacementExperiencePayout ?? 0)} 经验`;
          name.append(runtime);
        } else if (
          moduleRuntime?.killCharge !== undefined &&
          moduleRuntime.killChargeRequired !== undefined
        ) {
          const runtime = document.createElement("small");
          runtime.className = "slot-runtime";
          runtime.textContent =
            `灵魂力量 ${moduleRuntime.killCharge}/${moduleRuntime.killChargeRequired}`;
          name.append(runtime);
        } else if (moduleRuntime?.financeStatus) {
          const runtime = document.createElement("small");
          runtime.className = "slot-runtime";
          runtime.textContent = formatFinanceRuntime(moduleRuntime);
          name.append(runtime);
        } else if (moduleRuntime?.laborStatus) {
          const runtime = document.createElement("small");
          runtime.className = "slot-runtime";
          runtime.textContent = formatLaborRuntime(moduleRuntime, local);
          name.append(runtime);
        } else if (definition?.surfaceMining?.devour) {
          const runtime = document.createElement("small");
          runtime.className = "slot-runtime";
          runtime.textContent =
            local.surfaceMiningState.devourCooldownRemainingMs > 0
              ? `吞噬冷却 ${(local.surfaceMiningState.devourCooldownRemainingMs / 1_000).toFixed(1)}秒 · 累计 ${local.surfaceMiningState.devourCount}/10`
              : `吞噬 ${(local.surfaceMiningState.devourProgressMs / 1_000).toFixed(1)}/${(local.surfaceMiningState.devourRequiredMs / 1_000).toFixed(0)}秒 · 累计 ${local.surfaceMiningState.devourCount}/10`;
          name.append(runtime);
        } else if (
          definition?.id ===
          local.surfaceMiningState.conditionalEvolutionTargetId
        ) {
          const runtime = document.createElement("small");
          runtime.className = "slot-runtime evolved";
          runtime.textContent = "条件进阶完成 · 永久锁定";
          name.append(runtime);
        }

        const actions = document.createElement("div");
        actions.className = "slot-actions";
        if (!definition || definition.tier < 5) {
          const targetTier = definition ? definition.tier + 1 : 1;
          const upgradeCost =
            UPGRADE_GOLD_COSTS[targetTier as ModuleDefinition["tier"]];
          const upgrade = document.createElement("button");
          upgrade.className = "button slot-action primary";
          upgrade.type = "button";
          upgrade.textContent = `${definition ? "升阶" : "获得"} · ${upgradeCost}金币`;
          upgrade.disabled = local.scrap < upgradeCost;
          upgrade.addEventListener("click", () => {
            this.network.send({ type: "requestOffer", slot });
          });
          actions.append(upgrade);
        }
        if (definition && definition.tier < 6) {
          const reforge = document.createElement("button");
          reforge.className = "button slot-action ghost";
          reforge.type = "button";
          reforge.textContent = `重铸 · ${REFORGE_CRYSTAL_COSTS[definition.tier]}结晶`;
          reforge.disabled = local.core < REFORGE_CRYSTAL_COSTS[definition.tier];
          reforge.addEventListener("click", () => {
            this.network.send({ type: "requestReforge", slot });
          });
          actions.append(reforge);
        }
        if (definition?.tier === 5 || definition?.stagedAscension) {
          const targetId =
            definition.stagedAscension?.targetModuleId ?? definition.evolutionTo!;
          const evolution = getModule(targetId);
          const ascensionCost = Math.round(
            ASCENSION_CRYSTAL_COST *
              (definition.stagedAscension?.costMultiplier ?? 1),
          );
          const evolutionOwned = local.modules.includes(evolution.id);
          const ascend = document.createElement("button");
          ascend.className = "button slot-action ascend";
          ascend.type = "button";
          renderTieredModuleText(
            ascend,
            evolutionOwned
              ? `${evolution.name} · 已拥有`
              : `升格为 ${evolution.name} · ${ascensionCost}结晶`,
          );
          ascend.disabled =
            evolutionOwned || local.core < ascensionCost;
          ascend.addEventListener("click", () => {
            if (
              window.confirm(
                `升格后该槽位永久锁定，无法重铸。\n\n` +
                  `${definition.name} → ${evolution.name}`,
              )
            ) {
              this.network.send({ type: "ascendAbility", slot });
            }
          });
          actions.append(ascend);
        }
        if (definition?.tier === 6 || local.lockedSlots[slot]) {
          const locked = document.createElement("span");
          locked.className = "slot-locked";
          locked.textContent =
            definition?.id ===
            local.surfaceMiningState.conditionalEvolutionTargetId
              ? "✦ 条件进阶 · 永久锁定"
              : "✦ 神话升格 · 已锁定";
          actions.append(locked);
        }
        card.dataset.tier = String(definition?.tier ?? 0);
        card.append(index, name, actions);
        return card;
      }),
    );

    const attributeSummary = element<HTMLElement>("attribute-summary");
    const attributes = [
      `生命 ${Math.round(local.maxHealth)}`,
      `能量 ${Math.round(local.maxMana)}`,
      `生命恢复 ${local.combatStats.healthRegen.toFixed(1)}/秒`,
      `能量恢复 ${local.combatStats.manaRegen.toFixed(1)}/秒`,
      `攻击 ${Math.round(local.combatStats.damage)}`,
      `攻速 ${(1_000 / local.combatStats.attackIntervalMs).toFixed(2)}/秒`,
      `技能伤害 ×${local.combatStats.abilityDamageMultiplier.toFixed(2)}`,
      `移速 ${Math.round(local.combatStats.moveSpeed)}`,
      `护甲 ${Math.round(local.combatStats.armor)}`,
      ...(local.synergies.includes("perpetual_core")
        ? [`永动核心 ${local.combatStats.perpetualCoreStacks}/5层`]
        : []),
      ...(local.synergies.includes("became_cannon")
        ? [
            `炮台机动 ${Math.round(local.combatStats.turretMoveRatio * 100)}%基础移速`,
          ]
        : []),
      ...((local.combatStats.abilityDamageGrowth.world_devouring_desire ?? 0) > 0
        ? [
            `吞界之欲单发 +${local.combatStats.abilityDamageGrowth.world_devouring_desire}`,
          ]
        : []),
      `反隐 ${Math.round(local.combatStats.revealRadius)}`,
      `侦查 ${Math.round(local.combatStats.scoutingRadius)}`,
    ];
    attributeSummary.replaceChildren(
      ...attributes.map((value) => {
        const chip = document.createElement("span");
        chip.textContent = value;
        return chip;
      }),
    );

    const talentList = element<HTMLElement>("talent-list");
    talentList.replaceChildren(
      ...local.talents.map((talent) => {
        const button = document.createElement("button");
        button.className = "talent-button";
        button.type = "button";
        button.disabled =
          local.talentPoints <= 0 || talent.level >= talent.maxLevel;
        const name = document.createElement("strong");
        name.textContent =
          `${talent.name} ${talent.level}/${talent.maxLevel}`;
        const effect = document.createElement("small");
        effect.textContent = `${talent.description} · ${talent.effect}`;
        button.append(name, effect);
        button.addEventListener("click", () => {
          this.network.send({ type: "investTalent", talentId: talent.id });
        });
        return button;
      }),
    );
    talentList.dataset.points = String(local.talentPoints);

    const synergies = element<HTMLElement>("synergy-list");
    const visibleSynergyCards = local.synergies.map((id) => {
      const definition = SYNERGIES.find((synergy) => synergy.id === id);
      const card = document.createElement("article");
      card.className = "synergy-card";
      card.classList.toggle("apex", Boolean(definition?.apex));
      const name = document.createElement("strong");
      name.textContent =
        `${definition?.apex ? "✦ " : ""}${definition?.name ?? id}`;
      const recipe = document.createElement("small");
      recipe.className = "synergy-card-recipe";
      renderTieredModuleText(
        recipe,
        definition
          ? `配方：${definition.requirements.map(describeRequirement).join(" + ")}`
          : "配方未知",
      );
      const description = document.createElement("small");
      renderTieredModuleText(
        description,
        definition?.description ?? "羁绊效果已激活",
      );
      card.append(name, recipe, description);
      return card;
    });
    const hiddenSynergyCards = local.hiddenSynergies.map((synergy) => {
      const card = document.createElement("article");
      card.className = "synergy-card apex";
      const name = document.createElement("strong");
      name.textContent = `✦ 隐藏羁绊 · ${synergy.name}`;
      const description = document.createElement("small");
      renderTieredModuleText(description, synergy.description);
      card.append(name, description);
      return card;
    });
    const hiddenEffectCards = local.hiddenEffects.map((effect) => {
      const card = document.createElement("article");
      card.className = "synergy-card";
      const name = document.createElement("strong");
      name.textContent =
        `◇ 隐藏效果 · ${effect.name} · ${effect.active ? "已激活" : "锁定"}`;
      const description = document.createElement("small");
      renderTieredModuleText(description, effect.description);
      card.append(name, description);
      return card;
    });
    const repeatableHiddenEffectCards = local.repeatableHiddenEffects.map(
      (effect) => {
        const card = document.createElement("article");
        card.className = "synergy-card apex";
        const name = document.createElement("strong");
        name.textContent = `✦ ${effect.name} · 累计 ${effect.totalCount} 次`;
        const description = document.createElement("small");
        const tierSummary = Object.entries(effect.sourceTierCounts)
          .filter(([, count]) => count > 0)
          .map(([tier, count]) => `${tier}阶×${count}`)
          .join("、");
        description.textContent = tierSummary
          ? `${effect.description} · 来源 ${tierSummary}`
          : effect.description;
        card.append(name, description);
        return card;
      },
    );
    const hiddenProgressCards = (() => {
      if (local.hiddenSynergyProgress.length === 0) {
        return [];
      }
      const current = [...local.hiddenSynergyProgress]
        .reverse()
        .find((stage) => stage.active);
      const next = local.hiddenSynergyProgress.find((stage) => !stage.active);
      const card = document.createElement("article");
      card.className = "synergy-card";
      const name = document.createElement("strong");
      name.textContent = `隐藏阶段 · ${current?.name ?? "尚未激活"}`;
      const description = document.createElement("small");
      description.textContent = next
        ? `下一阶段「${next.name}」：${next.progress}/${next.requiredCount}（${next.eligibleTierLabel}赐福）`
        : "最终阶段已激活";
      card.append(name, description);
      return [card];
    })();
    const synergyCards = [
      ...hiddenProgressCards,
      ...repeatableHiddenEffectCards,
      ...hiddenSynergyCards,
      ...hiddenEffectCards,
      ...visibleSynergyCards,
    ];
    if (synergyCards.length === 0) {
      const empty = document.createElement("span");
      empty.className = "player-meta";
      empty.textContent = "尚未激活";
      synergies.replaceChildren(empty);
    } else {
      synergies.replaceChildren(...synergyCards);
    }

    const teamTechList = element<HTMLElement>("team-tech-list");
    teamTechList.replaceChildren(
      ...this.state.teamTech.map((tech) => {
        const definition = TEAM_TECH.find(
          (candidate) => candidate.id === tech.id,
        );
        const row = document.createElement("div");
        row.className = "team-tech-row";

        const copy = document.createElement("div");
        copy.className = "team-tech-copy";
        const name = document.createElement("strong");
        name.textContent =
          `${definition?.name ?? tech.id} · ${tech.level}/${tech.maxLevel}`;
        const effect = document.createElement("small");
        effect.textContent =
          tech.nextCost === null
            ? `${definition?.description ?? ""} · 已满级`
            : definition?.levels[tech.level]?.effect ?? "";
        copy.append(name, effect);

        const invest = document.createElement("button");
        invest.className = "button ghost";
        invest.type = "button";
        invest.textContent =
          tech.nextCost === null ? "满级" : `${tech.nextCost}金币`;
        invest.disabled =
          tech.nextCost === null || local.scrap < tech.nextCost;
        invest.addEventListener("click", () => {
          this.network.send({
            type: "investTeamTech",
            techId: tech.id,
          });
        });
        row.append(copy, invest);
        return row;
      }),
    );

    const support = element<HTMLElement>("team-support");
    const otherPlayers = this.state.players.filter(
      (player) => player.id !== local.id,
    );
    if (otherPlayers.length === 0) {
      support.replaceChildren();
    } else {
      const label = document.createElement("span");
      label.className = "hud-label";
      label.textContent = "已入库资源单向援助";
      support.replaceChildren(
        label,
        ...otherPlayers.map((player) =>
          this.createSupportRow(player, local),
        ),
      );
    }
  }

  private createSupportRow(
    player: PlayerPublicState,
    local: PlayerPublicState,
  ): HTMLElement {
    const row = document.createElement("div");
    row.className = "support-row";
    const name = document.createElement("span");
    const status =
      player.status === "downed"
        ? " · 倒地"
        : player.status === "lich"
          ? " · 巫妖"
        : "";
    name.textContent =
      `${player.name}${status} · ${player.scrap}金币 / ${player.core}结晶`;
    const actions = document.createElement("span");
    actions.className = "support-actions";
    const goldButton = document.createElement("button");
    goldButton.className = "button ghost";
    goldButton.type = "button";
    goldButton.textContent = "援助10金";
    goldButton.disabled = local.scrap < 10;
    goldButton.addEventListener("click", () => {
      this.network.send({
        type: "aidResource",
        targetPlayerId: player.id,
        resource: "gold",
        amount: 10,
      });
    });
    const crystalButton = document.createElement("button");
    crystalButton.className = "button ghost";
    crystalButton.type = "button";
    crystalButton.textContent = "援助1晶";
    crystalButton.disabled = local.core < 1;
    crystalButton.addEventListener("click", () => {
      this.network.send({
        type: "aidResource",
        targetPlayerId: player.id,
        resource: "crystal",
        amount: 1,
      });
    });
    actions.append(goldButton, crystalButton);
    row.append(name, actions);
    return row;
  }

  private updateDiscoveries(): void {
    const local = this.getLocalPlayer();
    if (!local) {
      return;
    }
    let changed = false;
    for (const moduleId of local.modules) {
      if (moduleId && !this.discoveredModules.has(moduleId)) {
        this.discoveredModules.add(moduleId);
        changed = true;
      }
    }
    for (const synergyId of local.synergies) {
      if (!this.discoveredSynergies.has(synergyId)) {
        this.discoveredSynergies.add(synergyId);
        changed = true;
      }
    }
    if (!changed) {
      return;
    }
    writeDiscoverySet(
      "bastion.phase3.discoveredModules",
      this.discoveredModules,
    );
    writeDiscoverySet(
      "bastion.phase3.discoveredSynergies",
      this.discoveredSynergies,
    );
    if (!this.codexPanel.classList.contains("hidden")) {
      this.renderCodex();
    }
  }

  private renderCodex(): void {
    const fixedAscensions = MODULES.filter(
      (module) => module.tier === 5 && module.evolutionTo,
    ).length;
    const conditionalAscensions = MODULES.filter(
      (module) => module.conditionalEvolution,
    ).length;
    element<HTMLElement>("codex-module-summary").textContent =
      `${MODULES.length} 项技能 · ${fixedAscensions} 条五升六` +
      (conditionalAscensions > 0
        ? ` · ${conditionalAscensions} 条条件进阶`
        : "");
    element<HTMLElement>("codex-synergy-summary").textContent =
      `${SYNERGIES.length} 个羁绊`;
    const effectiveModules = MODULES.filter((module) => module.tier !== 6);
    const discoveredEffectiveModules = effectiveModules.filter((module) =>
      this.discoveredModules.has(module.id),
    ).length;
    element<HTMLElement>("codex-progress").textContent =
      `已发现 ${discoveredEffectiveModules}/${effectiveModules.length} 秘印 · ` +
      `${this.discoveredSynergies.size}/${SYNERGIES.length} 羁绊`;

    const moduleCards = [...MODULES]
      .sort((a, b) => a.tier - b.tier || a.name.localeCompare(b.name, "zh-CN"))
      .map((module) =>
        createCodexCard(
          module.name,
          module.description,
          `${module.tier}阶 · ${abilityCategoryLabel(module.category)} · ` +
            formatAbilityDetails(module).join("；"),
          !module.hidden || this.discoveredModules.has(module.id),
          module.hidden && !this.discoveredModules.has(module.id)
            ? "隐藏秘印 · 尚未发现"
            : module.tier === 6
              ? MODULES.some(
                  (source) =>
                    source.conditionalEvolution?.targetModuleId === module.id,
                )
                ? "隐藏条件进阶 · 永久锁定"
                : "神话升格 · 图鉴公开"
              : "图鉴公开",
        ),
      );
    element<HTMLElement>("codex-modules").replaceChildren(...moduleCards);

    const synergyCards = SYNERGIES.map((synergy) =>
      createCodexCard(
        synergy.name,
        synergy.description,
        `配方：${synergy.requirements.map(describeRequirement).join(" + ")}`,
        true,
        synergy.apex ? "终局追求 · 图鉴公开" : "图鉴公开",
      ),
    );
    element<HTMLElement>("codex-synergies").replaceChildren(...synergyCards);
  }

  private renderOffer(): void {
    if (!this.offer) {
      return;
    }
    this.openOfferButton.classList.add("hidden");
    const modeLabel = this.offer.mode === "upgrade" ? "升阶" : "同阶重铸";
    element<HTMLElement>("offer-title").textContent =
      `第${this.offer.slot + 1}槽 · ${modeLabel} · ${this.offer.targetTier}阶` +
      (this.offer.scrapCost > 0 ? ` · 已支付${this.offer.scrapCost}金币` : "");
    const reroll = element<HTMLButtonElement>("reroll-offer");
    reroll.textContent = `再次重铸 · ${this.offer.rerollCost}结晶`;
    const local = this.getLocalPlayer();
    reroll.disabled = (local?.core ?? 0) < this.offer.rerollCost;
    const abandon = element<HTMLButtonElement>("abandon-offer");
    abandon.classList.toggle("hidden", !this.offer.canAbandon);
    abandon.textContent = `放弃 · 不返还${this.offer.scrapCost}金币`;
    const refreshHint = element<HTMLElement>("offer-refresh-hint");
    refreshHint.classList.toggle("hidden", !this.offer.canAbandon);
    refreshHint.textContent = this.offer.canAbandon
      ? "可以放弃本次升阶，但金币不返还；下一次升阶会重新随机候选。"
      : "";

    const candidates = element<HTMLElement>("offer-candidates");
    candidates.replaceChildren(
      ...this.offer.candidates.map((moduleId) => {
        const module = getModule(moduleId);
        const local = this.getLocalPlayer();
        const proposed = local ? [...local.modules] : [];
        if (proposed.length > this.offer!.slot) {
          proposed[this.offer!.slot] = moduleId;
        }
        const currentSynergies = new Set(local?.synergies ?? []);
        const activated = evaluateSynergies(proposed).filter(
          (synergy) => !currentSynergies.has(synergy.id),
        );
        const activatedIds = new Set(activated.map((synergy) => synergy.id));
        const card = document.createElement("button");
        card.type = "button";
        card.className = "offer-card";
        card.dataset.tier = String(module.tier);
        const name = document.createElement("strong");
        renderTieredModuleText(
          name,
          `${module.hidden ? "✦ 隐秘 · " : ""}${module.name}`,
        );
        const tags = document.createElement("small");
        tags.className = "offer-card-meta";
        tags.textContent =
          `${module.tier}阶 · ${abilityCategoryLabel(module.category)}`;
        const mechanics = document.createElement("ul");
        mechanics.className = "ability-mechanics";
        for (const detail of formatAbilityDetails(module)) {
          const item = document.createElement("li");
          item.textContent = detail;
          mechanics.append(item);
        }
        card.append(name, tags, mechanics);
        if ((module.tier === 5 && module.evolutionTo) || module.stagedAscension) {
          const targetId =
            module.stagedAscension?.targetModuleId ?? module.evolutionTo!;
          const ascensionCost = Math.round(
            ASCENSION_CRYSTAL_COST *
              (module.stagedAscension?.costMultiplier ?? 1),
          );
          const evolution = document.createElement("em");
          evolution.className = "evolution-preview";
          renderTieredModuleText(
            evolution,
            `固定升格：${getModule(targetId).name} · ${ascensionCost}结晶 · 升格后永久锁定`,
          );
          card.append(evolution);
        }
        const recipes = createOfferSynergyRecipes(
          moduleId,
          proposed.filter((id): id is string => Boolean(id)),
        );
        const recipePanel = document.createElement("span");
        recipePanel.className = "synergy-recipes";
        const recipeLabel = document.createElement("span");
        recipeLabel.className = "synergy-recipes-label";
        recipeLabel.textContent = "可组成羁绊";
        recipePanel.append(recipeLabel);
        if (recipes.length === 0) {
          const empty = document.createElement("span");
          empty.className = "synergy-recipe empty";
          empty.textContent = "暂无可组成羁绊";
          recipePanel.append(empty);
        } else {
          for (const recipe of recipes) {
            const line = document.createElement("span");
            const immediatelyActive = activatedIds.has(recipe.synergyId);
            line.className =
              `synergy-recipe${immediatelyActive ? " active" : ""}`;
            renderTieredModuleText(
              line,
              `${immediatelyActive ? "✓ " : ""}${recipe.formula}`,
            );
            recipePanel.append(line);
          }
        }
        card.classList.toggle("activates-combo", activated.length > 0);
        card.append(recipePanel);
        card.addEventListener("click", () => {
          if (!this.offer) {
            return;
          }
          this.network.send({
            type: "selectOffer",
            offerId: this.offer.id,
            moduleId,
          });
        });
        return card;
      }),
    );
    this.offerPanel.classList.remove("hidden");
  }

  private renderObjective(): void {
    if (!this.state) {
      return;
    }
    const panel = element<HTMLElement>("objective-panel");
    const available = this.state.objectives.filter(
      (objective) => objective.status !== "waiting",
    );
    const active = available.filter(
      (objective) => objective.status === "active",
    );
    const local = this.getLocalPlayer();
    const displayObjectives = window.matchMedia("(max-width: 760px)").matches
      ? active.length > 0
        ? active
        : available.slice(-1)
      : available;
    panel.classList.toggle("hidden", this.state.phase === "lobby");
    const list = element<HTMLElement>("objective-list");
    const victorySummary = document.createElement("div");
    victorySummary.className = "victory-progress-summary";
    victorySummary.textContent =
      `胜利进度 ${this.state.victoryProgress.completedObjectives}/${this.state.victoryProgress.requiredObjectives}` +
      ` · 最终首领 ${this.state.victoryProgress.finalBossDefeated ? "已击杀" : "未击杀"}`;
    list.replaceChildren(
      victorySummary,
      ...displayObjectives.map((objective) => {
          const item = document.createElement("div");
          item.className = "objective-item";
          item.dataset.status = objective.status;
          const heading = document.createElement("strong");
          heading.textContent =
            objective.status === "completed"
              ? `✓ ${objective.name}`
              : objective.status === "failed"
                ? `× ${objective.name}`
                : objective.name;
          const description = document.createElement("small");
          const healthText =
            objective.health !== undefined && objective.maxHealth !== undefined
              ? ` · 生命 ${Math.ceil(objective.health)}/${Math.ceil(objective.maxHealth)}`
              : "";
          const progressText =
            objective.id === "rift" && objective.status === "active"
              ? ` · ${objective.playerEngaged ? "玩家已进入裂口区域" : "等待玩家进入裂口区域"}` +
                ` · 击杀 ${objective.kills ?? 0}/${objective.requiredKills ?? 200}`
              : objective.id === "salvage" && objective.status === "active"
                ? objective.playerEngaged
                  ? ` · ${objective.routePhase === "to_core" ? "已入城，驶向核心" : "玩家已接应，驶向最近外门"}`
                  : " · 等待玩家前往炼金车处接应"
                : "";
          const navigationText =
            objective.status === "active" && local
              ? ` · ${formatObjectiveDirection(local.x, local.y, objective.x, objective.y)}`
              : "";
          description.textContent =
            objective.status === "active"
              ? `${objective.description}${healthText}${progressText}${navigationText}`
              : objective.status === "completed"
                ? "已完成"
                : "已失败";
          const track = document.createElement("div");
          track.className = "progress-track";
          const fill = document.createElement("span");
          const ratio =
            objective.status === "completed"
              ? 1
              : objective.id === "rift" && objective.requiredKills
                ? Math.min(1, (objective.kills ?? 0) / objective.requiredKills)
              : Math.min(1, objective.progressMs / objective.requiredMs);
          fill.style.width = `${ratio * 100}%`;
          track.append(fill);
          item.append(heading, description, track);
          return item;
        }),
    );
  }

  private renderResult(): void {
    if (!this.state) {
      return;
    }
    const victory = this.state.phase === "victory";
    const draw = this.state.phase === "draw";
    if (this.settledRoomCode !== this.state.roomCode) {
      this.settledRoomCode = this.state.roomCode;
      this.metaProgress = settleMetaProgress(
        this.metaProgress,
        this.state,
        this.network.playerId,
      );
      writeMetaProgress(this.metaProgress);
      this.renderMetaSummary();
    }
    element<HTMLElement>("result-title").textContent = victory
      ? "怪物城守卫战胜利"
      : draw
        ? "战局平局"
        : "核心失守";
    const local = this.getLocalPlayer();
    const completed = this.state.objectives.filter(
      (objective) => objective.status === "completed",
    ).length;
    element<HTMLElement>("result-summary").textContent =
      `${this.state.resultReason ?? ""} · 击杀 ${local?.kills ?? 0} · ` +
      `激活组合 ${(local?.synergies.length ?? 0) + (local?.hiddenSynergies.length ?? 0)} · 支线 ${completed}/3 · ` +
      `共同建设 ${this.state.teamTech.reduce((sum, tech) => sum + tech.level, 0)}/${this.state.teamTech.reduce((sum, tech) => sum + tech.maxLevel, 0)} · ` +
      `${DIFFICULTIES[this.state.difficulty].name}难度 · 种子 ${this.state.runSeed}`;

    const table = element<HTMLElement>("result-table");
    const header = document.createElement("div");
    header.className = "result-row result-header";
    for (const value of [
      "玩家",
      "伤害",
      "击杀",
      "救援",
      "支线",
      "援助/科技",
    ]) {
      const cell = document.createElement("span");
      cell.textContent = value;
      header.append(cell);
    }
    table.replaceChildren(
      header,
      ...[...this.state.players]
        .sort(
          (a, b) =>
            b.contribution.damageDealt - a.contribution.damageDealt,
        )
        .map((player) => {
          const row = document.createElement("div");
          row.className = "result-row";
          const values = [
            player.name,
            compactNumber(player.contribution.damageDealt),
            String(player.kills),
            String(player.contribution.revives),
            `${Math.round(player.contribution.objectiveMs / 1_000)}s`,
            `${player.contribution.goldDonated}金+${player.contribution.coreDonated}晶/${player.contribution.teamTechInvested}`,
          ];
          for (const value of values) {
            const cell = document.createElement("span");
            cell.textContent = value;
            row.append(cell);
          }
          return row;
        }),
    );

    const resultDamage = element<HTMLElement>("result-damage-breakdown");
    if (local) {
      this.renderDamageBreakdown(resultDamage, local);
    } else {
      resultDamage.replaceChildren();
    }

    const build = element<HTMLElement>("result-build");
    if (local) {
      const title = document.createElement("strong");
      title.textContent = "你的最终构筑";
      const modules = document.createElement("p");
      modules.textContent = local.modules
        .map((moduleId) => (moduleId ? getModule(moduleId).name : "空挂点"))
        .join(" · ");
      const legacy = document.createElement("p");
      legacy.className = "meta-reward";
      legacy.textContent =
        `局外进度：远征余烬 ${this.metaProgress.embers} · ` +
        `解锁 ${this.metaProgress.unlocks.join("、") || "尚无"}`;
      build.replaceChildren(title, modules, legacy);
    } else {
      build.replaceChildren();
    }
  }

  private renderDamageBreakdown(
    container: HTMLElement,
    player: PlayerPublicState,
  ): void {
    const total = player.contribution.damageDealt;
    const entries = new Map(
      (player.contribution.damageBySource ?? []).map((entry) => [
        entry.key,
        { ...entry },
      ]),
    );
    if (!entries.has("body")) {
      entries.set("body", {
        key: "body",
        kind: "body",
        abilityId: null,
        damage: 0,
      });
    }
    for (const moduleId of player.modules) {
      if (!moduleId) {
        continue;
      }
      const module = getModule(moduleId);
      if (module.combat) {
        const key = `ability:${moduleId}`;
        if (!entries.has(key)) {
          entries.set(key, {
            key,
            kind: "ability",
            abilityId: moduleId,
            damage: 0,
          });
        }
      }
      if (module.companion) {
        const key = `${module.companion.kind}:${moduleId}`;
        if (!entries.has(key)) {
          entries.set(key, {
            key,
            kind: module.companion.kind,
            abilityId: moduleId,
            damage: 0,
          });
        }
      }
    }
    const sortedEntries = [...entries.values()].sort(
      (a, b) =>
        b.damage - a.damage ||
        damageSourceOrder(a.kind) - damageSourceOrder(b.kind) ||
        damageSourceName(
          a.kind,
          a.abilityId,
          a.hiddenSourceId ?? null,
        ).localeCompare(
          damageSourceName(
            b.kind,
            b.abilityId,
            b.hiddenSourceId ?? null,
          ),
          "zh-CN",
        ),
    );

    const summary = document.createElement("div");
    summary.className = "damage-total";
    const summaryLabel = document.createElement("span");
    summaryLabel.textContent = "累计总伤害";
    const summaryValue = document.createElement("strong");
    summaryValue.textContent = compactNumber(total);
    const summaryNote = document.createElement("small");
    summaryNote.textContent =
      total > 0
        ? `${sortedEntries.filter((entry) => entry.damage > 0).length} 项有效伤害来源`
        : "尚未造成有效伤害";
    summary.append(summaryLabel, summaryValue, summaryNote);

    const header = document.createElement("div");
    header.className = "damage-row damage-header";
    for (const value of ["伤害来源", "伤害", "占比"]) {
      const cell = document.createElement("span");
      cell.textContent = value;
      header.append(cell);
    }

    container.replaceChildren(
      summary,
      header,
      ...sortedEntries.map((entry) => {
        const row = document.createElement("article");
        row.className = "damage-row";
        row.dataset.kind = entry.kind;

        const source = document.createElement("div");
        source.className = "damage-source";
        const name = document.createElement("strong");
        name.textContent = damageSourceName(
          entry.kind,
          entry.abilityId,
          entry.hiddenSourceId ?? null,
        );
        const kind = document.createElement("small");
        kind.textContent = damageSourceKindLabel(entry.kind);
        source.append(name, kind);

        const amount = document.createElement("strong");
        amount.className = "damage-amount";
        amount.textContent = compactNumber(entry.damage);

        const ratio = total > 0 ? entry.damage / total : 0;
        const percentage = document.createElement("strong");
        percentage.className = "damage-percentage";
        percentage.textContent = `${(ratio * 100).toFixed(1)}%`;

        const track = document.createElement("span");
        track.className = "damage-track";
        const fill = document.createElement("i");
        fill.style.width = `${Math.min(100, ratio * 100)}%`;
        track.append(fill);

        row.append(source, amount, percentage, track);
        return row;
      }),
    );
  }

  private renderMetaSummary(): void {
    const summary = document.getElementById("meta-summary");
    if (!summary) {
      return;
    }
    summary.textContent =
      `远征记录 ${this.metaProgress.runs} 局 / 胜利 ${this.metaProgress.wins} 局 · ` +
      `远征余烬 ${this.metaProgress.embers} · ` +
      `${this.metaProgress.unlocks.length} 项图鉴勋记`;
  }

  private async submitPlaytestReport(): Promise<void> {
    const status = element<HTMLElement>("playtest-submit-status");
    const button = element<HTMLButtonElement>("submit-playtest-report");
    if (
      !this.state ||
      (this.state.phase !== "victory" &&
        this.state.phase !== "draw" &&
        this.state.phase !== "defeat")
    ) {
      status.textContent = "请在本局结束后提交。";
      return;
    }
    const survey = this.readSurvey();
    if (!survey) {
      status.textContent = "请完成所有必填问卷项。";
      return;
    }
    if (!this.clientMetrics || this.clientMetrics.sampleCount < 3) {
      status.textContent = "客户端性能样本不足，请稍候几秒后再提交。";
      return;
    }
    const session = this.network.getReportSession();
    if (
      !session ||
      session.roomCode !== this.state.roomCode ||
      session.playerId !== this.network.playerId
    ) {
      status.textContent = "当前会话凭证不可用，无法验证报告。";
      return;
    }

    const submission: PlaytestSubmission = {
      reportVersion: 1,
      testerId: this.testerId,
      roomCode: session.roomCode,
      playerId: session.playerId,
      token: session.token,
      playerHostReceipt: this.network.getPlaytestReceipt(),
      client: this.clientMetrics,
      survey,
    };
    if (this.network.isPlayerHosted() && !submission.playerHostReceipt) {
      status.textContent = "房主正在生成本局报告凭证，请稍候几秒后重试。";
      return;
    }
    button.disabled = true;
    status.textContent = "正在提交……";
    try {
      const response = await fetch(
        this.network.getApiUrl("/playtest-report"),
        {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(submission),
        },
      );
      const result = (await response.json()) as {
        ok?: boolean;
        duplicate?: boolean;
        message?: string;
      };
      if (!response.ok || !result.ok) {
        throw new Error(result.message || `HTTP ${response.status}`);
      }
      status.textContent = result.duplicate
        ? "本局报告此前已提交。"
        : "提交成功，已计入后续真人验证汇总。";
      this.notice(status.textContent);
    } catch (error) {
      button.disabled = false;
      status.textContent =
        error instanceof Error ? `提交失败：${error.message}` : "提交失败。";
      this.notice(status.textContent, true);
    }
  }

  private readSurvey(): PlaytestSurvey | undefined {
    const replay = element<HTMLSelectElement>("survey-replay").value;
    const buildReadability = readRating("survey-build");
    const movementComfort = readRating("survey-movement");
    const cooperationValue = readRating("survey-cooperation");
    const failureClarity = readRating("survey-failure");
    const panel = element<HTMLSelectElement>("survey-panel").value;
    if (
      !["yes", "no"].includes(replay) ||
      !buildReadability ||
      !movementComfort ||
      !cooperationValue ||
      !failureClarity ||
      !["yes", "no"].includes(panel)
    ) {
      return undefined;
    }
    const reconnect = element<HTMLSelectElement>("survey-reconnect").value;
    return {
      immediateReplay: replay === "yes",
      buildReadability,
      movementComfort,
      cooperationValue,
      failureClarity,
      deviceClass: element<HTMLSelectElement>("survey-device")
        .value as PlaytestSurvey["deviceClass"],
      panelIssue: panel === "yes",
      backgroundReconnectPassed:
        reconnect === "pass" ? true : reconnect === "fail" ? false : null,
      notes: element<HTMLTextAreaElement>("survey-notes").value
        .trim()
        .slice(0, 500),
    };
  }

  private renderConnection(status: ConnectionStatus): void {
    const pill = element<HTMLElement>("connection-pill");
    pill.dataset.status = status;
    const labels: Record<ConnectionStatus, string> = {
      disconnected: "未连接",
      connecting: "连接中",
      connected: "已连接",
      reconnecting: "正在重连",
    };
    element<HTMLElement>("connection-label").textContent = labels[status];
  }

  private syncBattlePanelVisibility(): void {
    const inBattle = this.state?.phase === "playing";
    const showBuild = Boolean(inBattle && !this.buildCollapsed);
    const showOpenButton = Boolean(inBattle && this.buildCollapsed);
    const showTactics = Boolean(inBattle && !this.tacticsCollapsed);
    const showOpenTactics = Boolean(inBattle && this.tacticsCollapsed);
    this.buildPanel.classList.toggle("hidden", !showBuild);
    this.openBuildButton.classList.toggle("hidden", !showOpenButton);
    this.lanePanel.classList.toggle("hidden", !showTactics);
    this.openTacticsButton.classList.toggle("hidden", !showOpenTactics);
    this.syncBuildPage();
  }

  private syncBuildPage(): void {
    for (const button of document.querySelectorAll<HTMLButtonElement>(
      "[data-build-tab]",
    )) {
      const active = button.dataset.buildTab === this.activeBuildPage;
      button.classList.toggle("active", active);
      button.setAttribute("aria-selected", String(active));
    }
    for (const page of document.querySelectorAll<HTMLElement>(
      "[data-build-page]",
    )) {
      page.classList.toggle(
        "hidden",
        page.dataset.buildPage !== this.activeBuildPage,
      );
    }
  }

  private getLocalPlayer(): PlayerPublicState | undefined {
    return this.state?.players.find(
      (player) => player.id === this.network.playerId,
    );
  }

  private getLocalLaneId(): LaneId | undefined {
    const local = this.getLocalPlayer();
    if (!local || local.spaceId !== "surface") {
      return undefined;
    }
    return this.getClosestLaneId(local.x, local.y);
  }

  private getClosestLaneId(x: number, y: number): LaneId | undefined {
    return this.state?.lanes
      .map((lane) => ({
        id: lane.id,
        distance: Math.hypot(lane.x - x, lane.y - y),
      }))
      .sort((a, b) => a.distance - b.distance)[0]?.id;
  }

  private readName(): string {
    const name = this.playerName.value.trim().slice(0, 16) || "史莱姆学徒";
    this.playerName.value = name;
    localStorage.setItem("bastion.phase1.playerName", name);
    return name;
  }

  private notice(message: string, danger = false): void {
    const notice = document.createElement("div");
    notice.className = "notice";
    if (danger) {
      notice.style.borderColor = "rgba(255, 109, 125, 0.35)";
      notice.style.color = "#ffd7dc";
    }
    notice.textContent = message;
    element<HTMLElement>("notice-stack").append(notice);
    window.setTimeout(() => notice.remove(), 3_200);
  }

  private setError(message: string): void {
    this.connectError.textContent = message;
  }

  private clearError(): void {
    this.connectError.textContent = "";
  }
}

export function formatObjectiveDirection(
  playerX: number,
  playerY: number,
  objectiveX: number,
  objectiveY: number,
): string {
  const dx = objectiveX - playerX;
  const dy = objectiveY - playerY;
  const angle = Math.atan2(dy, dx);
  const index = Math.round(angle / (Math.PI / 4) + 8) % 8;
  const direction = ["东", "东南", "南", "西南", "西", "西北", "北", "东北"][index];
  return `${direction}方向 · 距离 ${Math.round(Math.hypot(dx, dy))}`;
}

function element<T extends HTMLElement>(id: string): T {
  const found = document.getElementById(id);
  if (!found) {
    throw new Error(`Missing UI element: #${id}`);
  }
  return found as T;
}

function mergeState(
  previous: PublicRoomState | undefined,
  incoming: PublicRoomState,
): PublicRoomState {
  if (!previous) {
    return incoming;
  }
  return {
    ...incoming,
    enemies: incoming.enemies ?? previous.enemies,
    abilityEntities:
      incoming.abilityEntities ?? previous.abilityEntities,
  };
}

function formatTime(milliseconds: number): string {
  const minutes = Math.floor(milliseconds / 60_000);
  const seconds = Math.floor((milliseconds % 60_000) / 1_000);
  return `${minutes}:${seconds.toString().padStart(2, "0")}`;
}

function laneName(laneId: LaneId): string {
  return {
    north: "北路",
    east: "东路",
    south: "南路",
    west: "西路",
  }[laneId];
}

function signalLabel(kind: "danger" | "assist" | "objective"): string {
  return {
    danger: "道路告急",
    assist: "请求支援",
    objective: "集合做支线",
  }[kind];
}

function abilityCategoryLabel(
  category: ModuleDefinition["category"],
): string {
  return {
    attack: "攻击",
    area: "群攻",
    elite_attack: "特攻",
    labor: "打工",
    mining: "采矿",
    finance: "理财",
    pasture: "牧灵",
    archaeology: "封存兼容",
    enhancement: "强化",
    follower: "随从",
    summon: "召唤",
    healing: "治疗",
    growth: "成长",
    one_shot: "单次",
    guard: "护卫",
    special: "特殊",
  }[category];
}

function formatPercent(value: number): string {
  const percent = Math.round(value * 100_000) / 1_000;
  return `${percent > 0 ? "+" : ""}${percent}%`;
}

function formatRuntimeNumber(value: number): string {
  return Number.isInteger(value)
    ? String(value)
    : value.toFixed(2).replace(/\.?0+$/, "");
}

function financeResourceLabel(resource: "gold" | "crystal"): string {
  return resource === "gold" ? "金币" : "结晶";
}

function formatFinanceRuntime(state: ModuleSlotRuntimeState): string {
  const remaining = state.financeRemainingMs === undefined
    ? ""
    : ` · ${(state.financeRemainingMs / 1_000).toFixed(1)}秒`;
  if (state.financeStatus === "waiting_payment") {
    return (
      `等待支付 ${formatRuntimeNumber(state.financeRequiredAmount ?? 0)}` +
      `${state.financeWaitingResource ?? "资源"} · 当前 ` +
      `${formatRuntimeNumber(state.financeCurrentAmount ?? 0)} · ` +
      `还差 ${formatRuntimeNumber(state.financeShortfall ?? 0)}`
    );
  }
  if (state.financeStatus === "countdown") {
    if (state.financeStakeName) {
      return (
        `锁定 ${state.financeStakeName} ` +
        `${formatRuntimeNumber(state.financeStakeAmount ?? 0)} · ` +
        `成功率 ${formatRuntimeNumber(state.financeSuccessPercent ?? 0)}%` +
        remaining
      );
    }
    return `开奖倒计时${remaining}`;
  }
  if (state.financeStatus === "income") {
    return `随对局时间增长的周期收益${remaining}`;
  }
  if (state.financeStatus === "spend_accumulator") {
    const progress =
      `${formatRuntimeNumber(state.financeProgress ?? 0)}/` +
      `${formatRuntimeNumber(state.financeProgressTarget ?? 0)}`;
    const shortfall = (state.financeShortfall ?? 0) > 0
      ? ` · 金币不足，还差 ${formatRuntimeNumber(state.financeShortfall ?? 0)}`
      : "";
    return `累计消费 ${progress} · 每次 ${formatRuntimeNumber(state.financeTickCost ?? 0)}金币${shortfall}${remaining}`;
  }
  if (state.financeStatus === "charge") {
    return (
      `充能 ${formatRuntimeNumber(state.financeProgress ?? 0)}/` +
      `${formatRuntimeNumber(state.financeProgressTarget ?? 0)}${remaining}`
    );
  }
  if (state.financeStatus === "claimed") {
    return state.financeRewardClaimed ? "进入奖励已领取" : "等待进入奖励";
  }
  if (state.financeStatus === "replacement_reward") {
    return `实际替换离槽可得 ${formatRuntimeNumber(state.financeReplacementReward ?? 0)}结晶`;
  }
  return `周期扣款（资源最低为0）${remaining}`;
}

function formatLaborRuntime(
  state: ModuleSlotRuntimeState,
  player: PlayerPublicState,
): string {
  const cast = state.castProgress === undefined
    ? ""
    : ` · 技能释放 ${state.castProgress}/${state.castRequired ?? 5}`;
  if (state.laborStatus === "distance") {
    return (
      `步行余数 ${formatRuntimeNumber(state.laborProgress ?? 0)}/` +
      `${formatRuntimeNumber(state.laborProgressTarget ?? 1_000)} · ` +
      `累计 ${formatRuntimeNumber(state.laborSecondaryProgress ?? 0)}/` +
      `${formatRuntimeNumber(state.laborSecondaryTarget ?? 200_000)}${cast}`
    );
  }
  if (state.laborStatus === "circle") {
    const tracing = player.labor.circleCandidateActive ? "转圈中" : "等待起圈";
    return (
      `${tracing} · 累计 ${formatRuntimeNumber(state.laborProgress ?? 0)}/` +
      `${formatRuntimeNumber(state.laborProgressTarget ?? 300)}圈${cast}`
    );
  }
  if (state.laborStatus === "stationary") {
    return (
      `静止收益 ${(Math.max(0, state.laborRemainingMs ?? 0) / 1_000).toFixed(1)}秒 · ` +
      `移动扣款 ${(Math.max(0, state.laborMovingRemainingMs ?? 0) / 1_000).toFixed(1)}秒 · ` +
      `连续静止 ${formatRuntimeNumber(state.laborSecondaryProgress ?? 0)}/` +
      `${formatRuntimeNumber(state.laborSecondaryTarget ?? 360_000)}毫秒${cast}`
    );
  }
  if (state.laborStatus === "route") {
    return (
      `起点：${state.laborLabel ?? "尚未武装"} · 单程 ` +
      `${formatRuntimeNumber(state.laborProgress ?? 0)}/` +
      `${formatRuntimeNumber(state.laborProgressTarget ?? 100)}${cast}`
    );
  }
  return (
    `烧烤额外金币 ${formatRuntimeNumber(state.laborProgress ?? 0)}/` +
    `${formatRuntimeNumber(state.laborProgressTarget ?? 1_000)}${cast}`
  );
}

function formatAbilityDetails(
  module: ModuleDefinition,
  minerSetActive = false,
): string[] {
  const details: string[] = [];
  if (module.labor) {
    const labor = module.labor;
    if (labor.kind === "movement_distance") {
      details.push(`每步行${labor.payoutDistance}距离获得${labor.goldPerPayout}金币`);
      details.push(`累计${labor.achievementDistance}距离获得永久隐藏效果`);
    } else if (labor.kind === "circle_traversal") {
      details.push(`每完成有效圆周获得${labor.goldPerCompletion}金币`);
      details.push(`累计${labor.achievementCount}圈获得永久隐藏效果`);
    } else if (labor.kind === "stationary_work") {
      details.push(`每静止${labor.payoutIntervalMs / 1_000}秒获得${labor.goldPerPayout}金币`);
      details.push(`自主移动每${labor.movingDrainIntervalMs / 1_000}秒扣${labor.movingDrainGold}金币`);
    } else if (labor.kind === "route_leg") {
      details.push(`中央水晶与最外门每单程获得${labor.goldPerLeg}金币`);
      details.push(`累计${labor.achievementLegs}单程获得永久隐藏效果`);
    } else if (labor.kind === "gate_to_gate_patrol") {
      details.push(`每从一个城门巡逻到另一个城门获得${labor.goldPerPatrol}金币`);
    } else if (labor.kind === "repair_payout") {
      details.push(`每完成${labor.repairPerPayout}点修复获得${labor.goldPerPayout}金币`);
    } else if (labor.kind === "team_repair_payout") {
      details.push(
        `自身与${labor.allyRadius}范围内友方累计修复${labor.repairPerPayout}点，获得${labor.goldPerPayout}金币`,
      );
      details.push(`自身修复按${labor.ownRepairMultiplier}倍计入`);
    } else {
      details.push(`每次归属击杀额外获得${labor.goldPerOwnedKill}金币`);
      details.push(`累计额外金币达到${labor.achievementBonusGold}获得永久隐藏效果`);
    }
  }
  if (module.experienceVault) {
    const vault = module.experienceVault;
    const evolution = vault.evolution.kind === "fixed"
      ? `储满后进阶为${getModule(vault.evolution.targetModuleId).name}`
      : `储满后${vault.evolution.branches
          .map(
            (branch) =>
              `${branch.weight}%进阶为${getModule(branch.targetModuleId).name}`,
          )
          .join("、")}`;
    details.push(`吸收正经验，上限 ${vault.capacity} · ${evolution}`);
    details.push(
      `非条件替换返还已储存经验的 ${Math.round(vault.replacementPayoutMultiplier * 100)}%`,
    );
  }
  if (module.killCharge) {
    details.push(
      `本槽直接击杀累计灵魂力量 ${module.killCharge.requiredCount} 点后进阶为` +
        getModule(module.killCharge.targetModuleId).name,
    );
  }
  if (module.financeState) {
    const finance = module.financeState;
    if (finance.timeScaledIncome) {
      details.push(
        `每 ${(finance.timeScaledIncome.intervalMs / 1_000).toFixed(0)} 秒按对局分钟数向上取整获得金币`,
      );
    }
    if (finance.resourceGate) {
      details.push(
        `拥有 ${finance.resourceGate.cost} ${financeResourceLabel(finance.resourceGate.resource)}时立即支付并进入下一状态`,
      );
    }
    if (finance.timedOutcome?.kind === "weighted_branches") {
      details.push(
        `${finance.timedOutcome.delayMs / 1_000} 秒后服务器确定性开奖：` +
          finance.timedOutcome.branches
            .map((branch) => `${branch.weight}%→${getModule(branch.targetModuleId).name}`)
            .join("、"),
      );
    }
    if (finance.onEnterGrant) {
      const grants = [
        ...(finance.onEnterGrant.resources ?? []).map(
          (grant) =>
            `${grant.amount}${financeResourceLabel(grant.resource)}`,
        ),
        finance.onEnterGrant.count
          ? `${finance.onEnterGrant.count}层幸运星`
          : undefined,
      ].filter((entry): entry is string => Boolean(entry));
      details.push(`进入时立即获得 ${grants.join("、")}`);
    }
    if (finance.timedTransition) {
      details.push(
        `${finance.timedTransition.delayMs / 1_000} 秒后变为${getModule(finance.timedTransition.targetModuleId).name}`,
      );
    }
    if (finance.periodicDrain) {
      details.push(
        `每 ${finance.periodicDrain.intervalMs / 1_000} 秒扣除 ${finance.periodicDrain.amount} ${financeResourceLabel(finance.periodicDrain.resource)}，最低 ${finance.periodicDrain.floor}`,
      );
    }
    if (finance.periodicSpendAccumulator) {
      const accumulator = finance.periodicSpendAccumulator;
      details.push(
        `每 ${accumulator.intervalMs / 1_000} 秒尝试消费 ${accumulator.costPerTick}` +
          `${financeResourceLabel(accumulator.resource)}，累计 ${accumulator.threshold} 后` +
          `获得 ${accumulator.thresholdReward.minInclusive}—${accumulator.thresholdReward.maxInclusive}` +
          `${financeResourceLabel(accumulator.thresholdReward.resource)}，` +
          `${accumulator.rareTransition.successPercent}%变为` +
          getModule(accumulator.rareTransition.targetModuleId).name,
      );
    }
    if (finance.periodicCharge) {
      details.push(
        `每 ${finance.periodicCharge.intervalMs / 1_000} 秒充能 ` +
          `${finance.periodicCharge.amountPerTick}，达到 ${finance.periodicCharge.threshold} 后变为` +
          getModule(finance.periodicCharge.targetModuleId).name,
      );
    }
    if (finance.onReplacementReward) {
      details.push(
        `实际被其他技能替换离槽时获得 ${finance.onReplacementReward.amount}` +
          financeResourceLabel(finance.onReplacementReward.resource),
      );
    }
    if (finance.randomStake) {
      details.push(
        `从 ${finance.randomStake.targetPool.length} 项可写属性与资源中等概率锁定一项，扣除当前值的 ${Math.round(finance.randomStake.ratio * 100)}%`,
      );
    }
    if (finance.timedOutcome?.kind === "stake_resolution") {
      const probability = finance.timedOutcome.fixedSuccessPercent !== undefined
        ? `${finance.timedOutcome.fixedSuccessPercent}%`
        : `10% + 每层幸运星1%（最高${finance.timedOutcome.dynamicSuccess?.maxPercent ?? 100}%）`;
      details.push(
        `${finance.timedOutcome.delayMs / 1_000} 秒后以 ${probability} 开奖；成功返还锁定扣除量的 ${finance.timedOutcome.stakeReturnMultiplier} 倍`,
      );
    }
  }
  if (module.surfaceMining) {
    const mining = module.surfaceMining;
    const scalable = !mining.devour && !mining.contactAttack;
    const effectMultiplier = minerSetActive && scalable ? 2 : 1;
    if ((mining.speedBonus ?? 0) > 0) {
      details.push(
        `采矿结算速率 +${Math.round((mining.speedBonus ?? 0) * effectMultiplier * 100)}%`,
      );
    }
    if ((mining.goldYieldBonus ?? 0) > 0) {
      details.push(
        `采矿最终金币 +${Math.round((mining.goldYieldBonus ?? 0) * effectMultiplier * 100)}%`,
      );
    }
    if ((mining.conditionalRevealRadiusBonus ?? 0) > 0) {
      details.push(
        `已有反隐范围 +${Math.round((mining.conditionalRevealRadiusBonus ?? 0) * effectMultiplier * 100)}%（无反隐时仍为0）`,
      );
    }
    if ((mining.crystalYieldRatio ?? 0) > 0) {
      details.push(
        `普通采矿按最终金币的 ${Math.round((mining.crystalYieldRatio ?? 0) * effectMultiplier * 100)}% 获得结晶`,
      );
    }
    if (mining.manaPoweredSpeed) {
      details.push(
        `魔力≥${mining.manaPoweredSpeed.manaCostPerSettlement}时采矿速率 +${Math.round(mining.manaPoweredSpeed.speedBonus * effectMultiplier * 100)}%；成功结算消耗 ${mining.manaPoweredSpeed.manaCostPerSettlement} 魔力`,
      );
    }
    if (mining.devour) {
      details.push(
        `连续处于矿脉范围 ${(mining.devour.channelMs / 1_000).toFixed(0)}秒后吞噬全部储量；成功后冷却 ${(mining.devour.cooldownMs / 1_000).toFixed(0)}秒`,
      );
      details.push(
        `每次成功基础移速永久 -${Math.round(mining.devour.moveSpeedPenaltyPerSuccess * 100)}个百分点；第10次同槽进阶`,
      );
    }
    if (mining.contactAttack) {
      details.push(
        `基础移速固定为初始的 ${Math.round(mining.contactAttack.fixedBaseMoveSpeedRatio * 100)}%；实际移动扫掠造成 ${mining.contactAttack.damage} 技能伤害`,
      );
      details.push(
        `每名敌人 ${(mining.contactAttack.enemyCooldownMs / 1_000).toFixed(1)}秒受击一次；非首领击退 ${mining.contactAttack.knockbackDistance}，击杀恢复 ${mining.contactAttack.healOnKill} 生命`,
      );
    }
    if ((mining.extraGoldFlat ?? 0) > 0) {
      details.push(`每次完整采集结算额外获得 ${(mining.extraGoldFlat ?? 0) * effectMultiplier} 金币`);
    }
    if ((mining.extraGoldMultiplier ?? 0) > 0) {
      details.push(
        `每次采集额外金币为基础量的 ${Math.round((mining.extraGoldMultiplier ?? 0) * effectMultiplier * 100)}%`,
      );
    }
    if ((mining.settlementIntervalMultiplier ?? 1) < 1) {
      details.push(
        `个人采集结算间隔缩短 ${Math.round((1 - (mining.settlementIntervalMultiplier ?? 1)) * 100)}%`,
      );
    }
    if ((mining.revealRadiusAdd ?? 0) > 0) {
      details.push(`反隐范围 +${mining.revealRadiusAdd}`);
    }
    if ((mining.scoutingRadiusAdd ?? 0) > 0) {
      details.push(`侦查范围 +${mining.scoutingRadiusAdd}`);
    }
  }
  if (module.combat) {
    const combat = module.combat;
    const target = {
      random: "随机单体",
      closest: "最近目标",
      farthest: "最远目标",
      highest_health: "生命值最高目标",
      lowest_health: "生命值最低目标",
      closest_to_core: "最接近基地的目标",
      fastest: "移动速度最快的目标",
      alternating_extremes: "最远与最近目标交替",
      random_area: "随机区域",
      elite_only: "仅精英/首领",
      boss_only: "仅首领",
    }[combat.target];
    details.push(
      `${target}：每${formatAttackIntervalSeconds(combat.intervalMs)}秒造成` +
        `${combat.damage}点伤害` +
        ((combat.projectiles ?? 1) > 1 ? `，发射${combat.projectiles}次` : ""),
    );
    details.push(
      `射程 ${combat.range}` +
        (combat.radius ? ` · 覆盖半径 ${combat.radius}` : "") +
        (combat.chainTargets ? ` · 弹射 ${combat.chainTargets}` : "") +
        (combat.pierceTargets ? ` · 穿透 ${combat.pierceTargets}` : ""),
    );
    if (combat.slowRatio) {
      details.push(
        `减速 ${Math.round(combat.slowRatio * 100)}% / ` +
          `${((combat.slowDurationMs ?? 0) / 1_000).toFixed(1)}秒`,
      );
    }
    if (combat.stunDurationMs) {
      details.push(
        `眩晕 ${(combat.stunDurationMs / 1_000).toFixed(2)}秒` +
          (combat.stunBosses === false ? "（不作用于首领）" : ""),
      );
    }
    if (combat.manaCost) {
      details.push(`每次发动消耗 ${combat.manaCost} 魔力`);
    }
  }
  if (module.economy) {
    const economy = module.economy;
    const trigger = {
      periodic: `每${((economy.intervalMs ?? 0) / 1_000).toFixed(1)}秒`,
      attack: "有效攻击时（受内部冷却限制）",
      repair: "产生有效修理时",
      excavate: "旧作业事件",
      move_distance: `每移动 ${economy.distance ?? 180} 距离`,
    }[economy.trigger];
    details.push(
      `${trigger}：+${economy.gold ?? 0}金币 / +${economy.experience ?? 0}灵息`,
    );
  }
  if (module.companion) {
    const companion = module.companion;
    const kind = {
      pasture: "牧灵",
      follower: "随从",
      summon: "召唤物",
      guard: "护卫",
    }[companion.kind];
    details.push(
      `${kind} ×${companion.count}` +
        (companion.ethereal ? " · 无实体/不可选中" : "") +
        (companion.damage
          ? ` · ${companion.damage}伤害/${formatAttackIntervalSeconds(
              companion.intervalMs ?? 1_000,
            )}秒`
          : "") +
        (companion.health ? ` · ${companion.health}生命/${companion.armor ?? 0}护甲` : ""),
    );
    if (companion.incomeGold) {
      details.push(
        `每${((companion.incomeIntervalMs ?? 0) / 1_000).toFixed(1)}秒带回 ` +
          `${companion.incomeGold}金币`,
      );
    }
    if (companion.manaCost) {
      details.push(
        `每批消耗 ${companion.manaCost}魔力 · 冷却 ` +
          `${((companion.spawnIntervalMs ?? 0) / 1_000).toFixed(1)}秒`,
      );
    }
    if (companion.respawnMs) {
      details.push(`死亡后 ${(companion.respawnMs / 1_000).toFixed(1)}秒复活`);
    }
    if (companion.movement?.kind === "player_centered_wander") {
      details.push(
        `玩家周围${companion.movement.wanderRadius}范围随机游荡 · ` +
          `游荡速度为玩家移速${Math.round(companion.movement.wanderSpeedRatio * 100)}% · ` +
          `超过${companion.movement.returnTriggerRadius}后以${Math.round(
            companion.movement.returnSpeedRatio * 100,
          )}%速度追赶至${companion.movement.returnReleaseRadius}范围`,
      );
    } else if (companion.movement?.kind === "player_centered_random") {
      details.push(
        `玩家周围${companion.movement.radius}范围随机移动 · ` +
          `移动速度为玩家基础移速${Math.round(
            companion.movement.speedBaseRatio * 100,
          )}%`,
      );
    }
  }
  if (module.healing) {
    const healingTarget = {
      self: "自身",
      lowest_ally: "生命最低的友军",
      team: "全队",
      core: "中央水晶",
    }[module.healing.target];
    details.push(
      `每${(module.healing.intervalMs / 1_000).toFixed(1)}秒治疗 ` +
        `${module.healing.amount} · 目标 ${healingTarget}`,
    );
  }
  for (const mechanic of module.mechanics ?? []) {
    if (mechanic.kind === "undead_summon") {
      details.push(
        `每${formatAttackIntervalSeconds(mechanic.intervalMs)}秒消耗` +
          `${mechanic.manaCost}魔力召唤${getUndeadUnit(mechanic.unitId).name}`,
      );
    } else if (mechanic.kind === "lich_form") {
      details.push(
        `${mechanic.mode === "permanent" ? "永久" : "死亡期间"}巫妖形态 · ` +
          `亡灵生命与攻击×${mechanic.undeadHealthDamageMultiplier} · ` +
          `亡灵召唤间隔×${mechanic.undeadSummonIntervalMultiplier}`,
      );
    } else if (mechanic.kind === "death_guard") {
      details.push(
        `濒死保护：冷却${formatAttackIntervalSeconds(mechanic.cooldownMs)}秒 · ` +
          `无敌${formatAttackIntervalSeconds(mechanic.invulnerabilityMs)}秒 · ` +
          `${formatAttackIntervalSeconds(mechanic.recoveryDurationMs)}秒内恢复` +
          `${Math.round(mechanic.recoveryHealthRatio * 100)}%最大生命`,
      );
    } else if (mechanic.kind === "periodic_target_damage") {
      details.push(
        `每${formatAttackIntervalSeconds(mechanic.intervalMs)}秒对范围` +
          `${mechanic.range}内一个随机${mechanic.target === "heavy" ? "重型单位" : "敌人"}` +
          `造成${mechanic.damage}点伤害`,
      );
    } else if (mechanic.kind === "companion_aura") {
      details.push(
        `召唤/护卫光环 ${mechanic.range}：全部战斗属性 +` +
          `${Math.round((mechanic.statMultiplier - 1) * 100)}%，进出保持生命比例`,
      );
    } else if (mechanic.kind === "periodic_reroll") {
      details.push(
        `每${formatAttackIntervalSeconds(mechanic.intervalMs)}秒同阶重随` +
          `${mechanic.excludeSelf ? "除自身外的" : ""}全部非六阶技能`,
      );
    } else if (mechanic.kind === "random_skill_cast") {
      details.push(
        `每${formatAttackIntervalSeconds(mechanic.intervalMs)}秒释放随机` +
          `${mechanic.tiers.join("/")}阶可释放技能`,
      );
    } else if (mechanic.kind === "enemy_conversion") {
      details.push(
        `每${formatAttackIntervalSeconds(mechanic.intervalMs)}秒转化范围` +
          `${mechanic.range}内一个普通敌人` +
          (mechanic.statMultiplier > 1
            ? `，全部战斗属性 +${Math.round((mechanic.statMultiplier - 1) * 100)}%`
            : ""),
      );
    } else if (mechanic.kind === "black_hole") {
      details.push(
        `每${formatAttackIntervalSeconds(mechanic.spawnIntervalMs)}秒生成直径` +
          `${mechanic.diameter}黑洞，持续${formatAttackIntervalSeconds(mechanic.durationMs)}秒；` +
          `每${formatAttackIntervalSeconds(mechanic.tickIntervalMs)}秒造成${mechanic.damage}点伤害并牵引普通敌人`,
      );
    } else if (mechanic.kind === "pasture_effect_amplifier") {
      details.push(
        `牧灵属性与恢复效果提高${Math.round(
          (mechanic.effectMultiplier - 1) * 100,
        )}%（同类增幅只取最高值）`,
      );
    } else if (mechanic.kind === "guard_periodic_area_damage") {
      details.push(
        `每${formatAttackIntervalSeconds(mechanic.intervalMs)}秒对范围` +
          `${mechanic.radius}内全部敌人造成${mechanic.damage}点伤害`,
      );
    } else if (mechanic.kind === "earth_spirit_summon") {
      details.push(
        `在玩家${mechanic.spawnRadius}范围内生成，出生点为固定活动中心 · ` +
          `${mechanic.alertRange}范围选择最近敌人 · 失去目标` +
          `${formatAttackIntervalSeconds(mechanic.returnDelayMs)}秒后强制返回`,
      );
    } else if (mechanic.kind === "follower_periodic_damage") {
      details.push(
        `随从每${formatAttackIntervalSeconds(mechanic.intervalMs)}秒从当前位置对` +
          `${mechanic.range}范围内一个随机敌人造成` +
          `${mechanic.hits > 1 ? `${mechanic.hits}×` : ""}${mechanic.damage}点伤害`,
      );
    } else if (mechanic.kind === "periodic_self_area_damage") {
      details.push(
        `每${formatAttackIntervalSeconds(mechanic.intervalMs)}秒对玩家周围` +
          `${mechanic.radius}范围内全部敌人造成${mechanic.damage}点伤害` +
          (mechanic.slowRatio
            ? `，减速${Math.round(mechanic.slowRatio * 100)}%持续` +
              `${formatAttackIntervalSeconds(mechanic.slowDurationMs ?? 0)}秒`
            : ""),
      );
      if (mechanic.healOnPositiveHit) {
        details.push(
          `本次范围结算造成任意正伤害后恢复 ${mechanic.healOnPositiveHit} 生命，命中多个仍只恢复一次`,
        );
      }
    } else if (mechanic.kind === "periodic_normal_execute_reward") {
      details.push(
        `每${formatAttackIntervalSeconds(mechanic.intervalMs)}秒秒杀` +
          `${mechanic.range}范围内一个随机普通敌人，并将自身本次正常击杀总收益替换为` +
          `${mechanic.rewardMultiplier}倍`,
      );
    } else if (mechanic.kind === "periodic_forced_aggro") {
      details.push(
        `每${formatAttackIntervalSeconds(mechanic.intervalMs)}秒迫使` +
          `${mechanic.range}范围内一个随机非首领敌人攻击自己；锁定期间移动速度降低` +
          `${Math.round(mechanic.slowRatio * 100)}%，优先级高于嘲讽`,
      );
    } else if (mechanic.kind === "ability_kill_growth") {
      details.push(
        `每累计${mechanic.killsPerSettlement}次由本技能直接伤害完成的有效击杀，` +
          `等概率永久获得最大生命值+${mechanic.maxHealthAdd}或攻击力+${mechanic.damageAdd}`,
      );
    } else if (mechanic.kind === "basic_attack_cannon") {
      details.push(
        `普攻炮击：消耗最多${Math.round(mechanic.maxManaCostRatio * 100)}%最大魔力 · ` +
          `基础直径${mechanic.baseDiameter} · 二段伤害为魔力消耗×${mechanic.secondaryDamagePerMana}` +
          (mechanic.diameterMultiplierPerManaRatio > 0
            ? ` · 每消耗1%最大魔力使范围增加${Math.round(mechanic.diameterMultiplierPerManaRatio)}%`
            : "") +
          ` · 基础攻击速度${Math.round(mechanic.baseAttackSpeedModifier * 100)}%`,
      );
    }
  }
  if (module.growth) {
    const growthBonus = module.growth.bonuses
      ? Object.entries(module.growth.bonuses)
          .map(([stat, amount]) => `+${amount} ${growthStatLabel(stat)}`)
          .join("、")
      : `+${module.growth.amount} ${growthStatLabel(module.growth.stat)}`;
    details.push(
      `局内永久成长：每 ${module.growth.every} ${growthEventLabel(module.growth.event)} ` +
        growthBonus +
        (module.growth.cap ? `，上限 ${module.growth.cap}` : ""),
    );
  }
  if (module.onAcquire) {
    const reward = module.onAcquire;
    details.push(
      "获得即生效，可反复获取：" +
        [
          reward.gold ? `${reward.gold}金币` : "",
          reward.experience ? `${reward.experience}灵息` : "",
          reward.crystals ? `${reward.crystals}结晶` : "",
          reward.damage ? `${reward.damage}攻击` : "",
          reward.maxHealth ? `${reward.maxHealth}生命上限` : "",
          reward.maxMana ? `${reward.maxMana}魔力上限` : "",
          reward.armor ? `${reward.armor}护甲` : "",
          reward.attackSpeedBonus
            ? `${formatPercent(reward.attackSpeedBonus)}普通攻击速度`
            : "",
          reward.abilityDamageBonus
            ? `${formatPercent(reward.abilityDamageBonus)}技能伤害`
            : "",
          reward.manaRegenMaxRatio
            ? `每秒恢复${formatPercent(reward.manaRegenMaxRatio)}最大魔力`
            : "",
          reward.currentLevelExperienceRatio
            ? `获得本级升级所需经验的${formatPercent(reward.currentLevelExperienceRatio)}`
            : "",
          reward.grantLevels ? `立即提升${reward.grantLevels}级并保留经验` : "",
        ]
          .filter(Boolean)
          .join("、"),
    );
  }
  const statDetails = formatStatBonuses(module);
  if (statDetails.length > 0) {
    details.push(`本体强化：${statDetails.join("、")}`);
  }
  if (module.special) {
    details.push(`规则效果：${specialBehaviorLabel(module.special)}`);
  }
  return details.length > 0 ? details : ["无持续效果；价值在获得时或特殊规则中结算"];
}

function growthStatLabel(stat: string): string {
  return {
    damage: "攻击力",
    maxHealth: "最大生命值",
    armor: "护甲",
    moveSpeed: "移动速度",
    manaMax: "最大魔力值",
    abilityDamage: "技能伤害",
    healthRegen: "生命恢复",
    manaRegen: "魔力恢复",
  }[stat] ?? "对应属性";
}

function growthEventLabel(event: NonNullable<ModuleDefinition["growth"]>["event"]): string {
  return {
    second: "秒",
    move_distance: "移动距离",
    attack: "次攻击",
    kill: "次击杀",
    repair: "点修理",
    damage_taken: "点实际承伤",
    excavate: "次旧作业",
    companion_income: "次牧灵产出",
  }[event];
}

function specialBehaviorLabel(
  special: NonNullable<ModuleDefinition["special"]>,
): string {
  return {
    pasture_inherits_attack: "牧灵继承一项攻击秘印",
    portal_on_hit: "攻击概率开启持续伤害裂隙",
    copy_random_ability: "复制已有秘印的一部分强化",
    emergency_overdrive: "低生命时自动过载",
    companion_rebirth_burst: "护卫重生时爆炸并治疗",
    all_chest_keys: "视作持有全部四色秘钥",
    summon_echoes: "召唤时产生额外战斗回响",
  }[special];
}

function formatStatBonuses(module: ModuleDefinition): string[] {
  const bonuses = module.bonuses;
  const values: string[] = [];
  if (bonuses.damageAdd) values.push(`攻击 ${signed(bonuses.damageAdd)}`);
  if (bonuses.damageMultiplier)
    values.push(`总伤害 +${Math.round(bonuses.damageMultiplier * 100)}%`);
  if (bonuses.attackDamageBonus)
    values.push(`攻击力 +${Math.round(bonuses.attackDamageBonus * 100)}%`);
  if (bonuses.abilityDamageBonus)
    values.push(`技能伤害 +${Math.round(bonuses.abilityDamageBonus * 100)}%`);
  if (bonuses.baseAttackSpeedModifier)
    values.push(`基础攻速 ${signed(Math.round(bonuses.baseAttackSpeedModifier * 100))}%`);
  if (bonuses.attackSpeedBonus)
    values.push(`攻击速度 ${signed(Math.round(bonuses.attackSpeedBonus * 100))}%`);
  if (bonuses.maxHealthAdd) values.push(`生命 ${signed(bonuses.maxHealthAdd)}`);
  if (bonuses.baseMaxHealthRatio)
    values.push(`基础生命上限 +${Math.round(bonuses.baseMaxHealthRatio * 100)}%`);
  if (bonuses.armorAdd) values.push(`护甲 ${signed(bonuses.armorAdd)}`);
  if (bonuses.moveSpeedAdd) values.push(`移速 ${signed(bonuses.moveSpeedAdd)}`);
  if (bonuses.moveSpeedMultiplier)
    values.push(
      `移速 ${signed(Math.round((bonuses.moveSpeedMultiplier - 1) * 100))}%`,
    );
  if (bonuses.manaMaxAdd) values.push(`魔力上限 ${signed(bonuses.manaMaxAdd)}`);
  if (bonuses.manaRegenAdd) values.push(`魔力恢复 ${signed(bonuses.manaRegenAdd)}`);
  if (bonuses.manaRegenMaxRatio)
    values.push(
      `每秒恢复 ${Math.round(bonuses.manaRegenMaxRatio * 100)}%最大魔力`,
    );
  if (bonuses.healthRegenMaxRatio)
    values.push(
      `每秒恢复 ${Math.round(bonuses.healthRegenMaxRatio * 100)}%最大生命`,
    );
  if (bonuses.dodgeAdd)
    values.push(`闪避率 +${Math.round(bonuses.dodgeAdd * 100)}%`);
  if (bonuses.goldMultiplier)
    values.push(`金币 +${Math.round(bonuses.goldMultiplier * 100)}%`);
  if (bonuses.companionDamageMultiplier)
    values.push(`伙伴伤害 +${Math.round(bonuses.companionDamageMultiplier * 100)}%`);
  if (bonuses.eliteDamageMultiplier)
    values.push(`精英伤害 +${Math.round(bonuses.eliteDamageMultiplier * 100)}%`);
  return values;
}

function signed(value: number): string {
  return value > 0 ? `+${value}` : String(value);
}

function compactNumber(value: number): string {
  const rounded = Math.round(value);
  if (rounded >= 1_000_000) {
    return `${(rounded / 1_000_000).toFixed(1)}m`;
  }
  if (rounded >= 1_000) {
    return `${(rounded / 1_000).toFixed(1)}k`;
  }
  return String(rounded);
}

function damageSourceOrder(kind: DamageSourceKind): number {
  return {
    body: 0,
    ability: 1,
    hidden_effect: 2,
    summon: 3,
    guard: 4,
    follower: 5,
    pasture: 6,
  }[kind];
}

function damageSourceKindLabel(kind: DamageSourceKind): string {
  return {
    body: "本体",
    ability: "技能",
    hidden_effect: "符文/羁绊",
    summon: "召唤物",
    guard: "护卫",
    follower: "随从",
    pasture: "牧灵游猎",
  }[kind];
}

function damageSourceName(
  kind: DamageSourceKind,
  abilityId: string | null,
  hiddenSourceId: string | null,
): string {
  if (kind === "body") {
    return "本体普通攻击";
  }
  if (kind === "hidden_effect") {
    if (!hiddenSourceId) {
      return "未知隐藏效果";
    }
    try {
      return getHiddenEffect(hiddenSourceId).name;
    } catch {
      try {
        return getHiddenSynergy(hiddenSourceId).name;
      } catch {
        return (
          SYNERGIES.find((synergy) => synergy.id === hiddenSourceId)?.name ??
          "未知符文效果"
        );
      }
    }
  }
  let moduleName = "未知秘印";
  if (abilityId) {
    try {
      moduleName = getModule(abilityId).name;
    } catch {
      moduleName =
        SYNERGIES.find((synergy) => synergy.id === abilityId)?.name ??
        abilityId;
    }
  }
  return kind === "ability"
    ? moduleName
    : `${moduleName} · ${damageSourceKindLabel(kind)}`;
}

function renderTierLabel(tier: PlaytestClientMetrics["tier"]): string {
  return {
    low: "低负载",
    balanced: "均衡",
    high: "高质量",
  }[tier];
}

function createTechnicalReport(
  state: PublicRoomState,
  clientMetrics: PlaytestClientMetrics | undefined,
): Record<string, unknown> {
  return {
    protocolVersion: state.protocolVersion,
    contentVersion: state.contentVersion,
    runSeed: state.runSeed,
    roomCode: state.roomCode,
    phase: state.phase,
    difficulty: state.difficulty,
    durationMs: state.elapsedMs,
    resultReason: state.resultReason,
    core: {
      health: Math.round(state.core.health),
      maxHealth: state.core.maxHealth,
    },
    objectives: state.objectives.map((objective) => ({
      id: objective.id,
      status: objective.status,
      progressMs: objective.progressMs,
    })),
    teamTech: state.teamTech,
    players: state.players.map((player) => ({
      id: player.id,
      name: player.name,
      kills: player.kills,
      modules: player.modules,
      synergies: [
        ...player.synergies,
        ...player.hiddenSynergies.map((synergy) => synergy.id),
      ],
      contribution: player.contribution,
    })),
    client: {
      metrics: clientMetrics,
    },
  };
}

function readRating(id: string): number | undefined {
  const value = Number(element<HTMLSelectElement>(id).value);
  return Number.isInteger(value) && value >= 1 && value <= 5
    ? value
    : undefined;
}

function createCodexCard(
  name: string,
  description: string,
  recipe: string,
  discovered: boolean,
  statusLabel?: string,
): HTMLElement {
  const card = document.createElement("article");
  card.className = "codex-card";
  card.dataset.discovered = String(discovered);
  const title = document.createElement("strong");
  renderTieredModuleText(title, name);
  const body = document.createElement("span");
  renderTieredModuleText(body, description);
  const details = document.createElement("small");
  renderTieredModuleText(details, recipe);
  const status = document.createElement("em");
  status.textContent =
    statusLabel ?? (discovered ? "已发现" : "尚未在本浏览器发现");
  card.append(title, body, details, status);
  return card;
}

function readMetaProgress(): MetaProgress {
  try {
    const parsed = JSON.parse(
      localStorage.getItem("bastion.phase3.meta") ?? "{}",
    ) as Partial<MetaProgress>;
    return {
      runs: Math.max(0, Math.floor(parsed.runs ?? 0)),
      wins: Math.max(0, Math.floor(parsed.wins ?? 0)),
      embers: Math.max(0, Math.floor(parsed.embers ?? 0)),
      unlocks: Array.isArray(parsed.unlocks)
        ? parsed.unlocks.filter(
            (value): value is string => typeof value === "string",
          )
        : [],
      settledRooms: Array.isArray(parsed.settledRooms)
        ? parsed.settledRooms.filter(
            (value): value is string => typeof value === "string",
          )
        : [],
    };
  } catch {
    return {
      runs: 0,
      wins: 0,
      embers: 0,
      unlocks: [],
      settledRooms: [],
    };
  }
}

function writeMetaProgress(progress: MetaProgress): void {
  localStorage.setItem("bastion.phase3.meta", JSON.stringify(progress));
}

function settleMetaProgress(
  previous: MetaProgress,
  state: PublicRoomState,
  playerId: string | undefined,
): MetaProgress {
  if (previous.settledRooms.includes(state.roomCode)) {
    return previous;
  }
  const local = state.players.find((player) => player.id === playerId);
  const victory = state.phase === "victory";
  const completed = state.objectives.filter(
    (objective) => objective.status === "completed",
  ).length;
  const next: MetaProgress = {
    runs: previous.runs + 1,
    wins: previous.wins + Number(victory),
    embers:
      previous.embers +
      (victory ? 5 : 2) +
      completed +
      Number(
        local?.contribution.firstAscensionAtMs !== null &&
          local?.contribution.firstAscensionAtMs !== undefined,
      ) *
        5,
    unlocks: [...previous.unlocks],
    settledRooms: [...previous.settledRooms, state.roomCode].slice(-40),
  };
  const unlock = (name: string, condition: boolean): void => {
    if (condition && !next.unlocks.includes(name)) {
      next.unlocks.push(name);
    }
  };
  unlock("远征见习", true);
  unlock("营地守望者", victory);
  unlock(
    "传说锻造师",
    local?.contribution.firstTierFiveAtMs !== null &&
      local?.contribution.firstTierFiveAtMs !== undefined,
  );
  unlock(
    "神话升格者",
    local?.contribution.firstAscensionAtMs !== null &&
      local?.contribution.firstAscensionAtMs !== undefined,
  );
  unlock("神话幸存者", victory && state.difficulty === "master");
  next.unlocks.sort((a, b) => a.localeCompare(b, "zh-CN"));
  return next;
}

function readDiscoverySet(key: string): Set<string> {
  try {
    const parsed = JSON.parse(localStorage.getItem(key) ?? "[]") as unknown;
    return new Set(
      Array.isArray(parsed)
        ? parsed.filter((value): value is string => typeof value === "string")
        : [],
    );
  } catch {
    return new Set();
  }
}

function writeDiscoverySet(key: string, values: Set<string>): void {
  localStorage.setItem(key, JSON.stringify([...values].sort()));
}

function getOrCreateTesterId(): string {
  const key = "bastion.phase2.testerId";
  const stored = localStorage.getItem(key);
  if (stored && /^[a-zA-Z0-9-]{8,64}$/.test(stored)) {
    return stored;
  }
  const created =
    crypto.randomUUID?.() ??
    `tester-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  localStorage.setItem(key, created);
  return created;
}

async function copyText(value: string): Promise<boolean> {
  try {
    if (navigator.clipboard) {
      await navigator.clipboard.writeText(value);
      return true;
    }
  } catch {
    // Fall through to the legacy path for non-secure LAN origins.
  }
  const textarea = document.createElement("textarea");
  textarea.value = value;
  textarea.style.position = "fixed";
  textarea.style.opacity = "0";
  document.body.append(textarea);
  textarea.select();
  const copied = document.execCommand("copy");
  textarea.remove();
  return copied;
}
